import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { detectInitialDialect, navigateTo } from './shared.js';
import { DiagnosticoPage, DiagnosticoConfirmado } from './diagnostico.jsx';
import { PrivacidadPage, AvisoLegalPage, CookiesPage } from './legal.jsx';
import { SofiaDemoPage } from './sofia-demo.jsx';

/* ---------- TWEAKS ---------- */
const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "dialect": "castellano",
  "phase": "pilot",
  "accentHue": 144,
  "priceMode": "current"
}/*EDITMODE-END*/;

/* ---------- SWITCH (the motif) ---------- */
// Knob layers ride a single <g> translate; animating cx on each circle
// individually desyncs the dashed inner from the outer disc.
function Switch({ on, size = 1, animated = false }) {
  const w = 80 * size, h = 42 * size, r = 18 * size;
  const offCx = r + 3;
  const travel = w - 2 * r - 6;
  const ease = 'cubic-bezier(.4, 0, .2, 1)';
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'inline-block', verticalAlign: 'middle', overflow: 'visible' }}>
      <defs>
        <linearGradient id={`knob-g-${size}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" />
          <stop offset="1" stopColor="#e8e4dc" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width={w - 2} height={h - 2} rx={h / 2}
      fill={on ? 'var(--on)' : 'var(--bg-2)'}
      stroke="var(--rule-strong)"
      style={{ transition: animated ? `fill .7s ${ease}` : 'none' }} />
      <g style={{
        transform: `translateX(${on ? travel : 0}px)`,
        transition: animated ? `transform .7s ${ease}` : 'none'
      }}>
        <circle cx={offCx} cy={h / 2} r={r}
        fill={`url(#knob-g-${size})`} stroke="var(--rule-strong)"
        style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,.15))' }} />
        <circle cx={offCx} cy={h / 2} r={r * 0.35} fill="none" stroke="var(--rule-strong)" strokeDasharray="2 2" />
      </g>
    </svg>);

}

/* ---------- Nav ---------- */
function Nav({ onOpenBooking }) {
  const [visible, setVisible] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lastScrollY = useRef(typeof window !== 'undefined' ? window.scrollY : 0);

  // Directional scroll tracker: hide on scroll-down past 80px, show on any scroll-up.
  // Always visible within the top 80px reveal-zone. No hysteresis — any UP movement reveals.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onScroll = () => {
      if (drawerOpen) return; // drawer-open escape hatch — don't react while drawer is open
      const cur = window.scrollY;
      const dy = cur - lastScrollY.current;
      lastScrollY.current = cur;
      if (cur < 80) {
        setVisible(true);            // always show in top reveal-zone
      } else if (dy < 0) {
        setVisible(true);            // any upward movement reveals immediately
      } else if (dy > 0) {
        setVisible(false);           // downward → hide
      }
      // dy === 0 falls through (no state change)
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [drawerOpen]);

  // While the mobile drawer is open: lock body scroll + close on Escape +
  // close if the viewport crosses past the 768px breakpoint (drawer becomes
  // display:none at that width — releasing the scroll-lock prevents a stuck body).
  useEffect(() => {
    if (!drawerOpen) return;
    setVisible(true); // force-show nav when drawer opens (safe at any scroll position)
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    const onResize = () => { if (window.innerWidth > 768) setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <>
      <div
        className="nav-shell"
        style={{ transform: visible ? 'translateY(0)' : 'translateY(-110%)' }}>
        <div className="nav-inner wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <a href="#top" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Switch on={true} size={0.5} />
            <span style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.02em' }}>EnAutomático</span>
          </a>
          <nav style={{ display: 'flex', gap: 28, fontSize: 14, color: 'var(--ink-2)' }}>
            <a href="#ejemplo" style={{ textDecoration: 'none' }}>Ejemplo de informe</a>
            <a href="#precios" style={{ textDecoration: 'none' }}>Precios</a>
            <a href="#faq" style={{ textDecoration: 'none' }}>FAQ</a>
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenBooking(); }} className="btn nav-cta" style={{ padding: '10px 18px', cursor: 'pointer', background: 'var(--ink)', color: 'var(--bg)', border: '1px solid var(--ink)' }}>
              Obtén tu Plan
              <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button
              type="button"
              className="mobile-drawer-toggle"
              aria-label={drawerOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={drawerOpen}
              aria-controls="mobile-drawer-panel"
              onClick={() => setDrawerOpen((v) => !v)}>
              <span className={'mobile-drawer-toggle-icon' + (drawerOpen ? ' is-open' : '')} aria-hidden="true">
                <span /><span /><span />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer overlay — hidden on desktop via CSS. */}
      <div
        id="mobile-drawer-panel"
        className={'mobile-drawer' + (drawerOpen ? ' open' : '')}
        role="dialog"
        aria-modal="true"
        aria-hidden={!drawerOpen}
        aria-label="Menú de navegación">
        <div className="mobile-drawer-backdrop" onClick={closeDrawer} />
        <div className="mobile-drawer-panel" role="document">
          <nav className="mobile-drawer-nav" aria-label="Principal">
            <a href="#ejemplo" onClick={closeDrawer}>Ejemplo de informe</a>
            <a href="#precios" onClick={closeDrawer}>Precios</a>
            <a href="#faq" onClick={closeDrawer}>FAQ</a>
          </nav>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); closeDrawer(); onOpenBooking(); }}
            className="btn mobile-drawer-cta"
            style={{ cursor: 'pointer', background: 'var(--ink)', color: 'var(--bg)', border: '1px solid var(--ink)' }}>
            Obtén tu Plan
            <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>
    </>);

}

/* ---------- HERO LATTICE — generative toggle field behind the H1 ---------- */
// Builds ~170 grid-aligned mini-switches imperatively, then auto-flips one at
// random every ~175–275 ms. Imperative DOM (vs React-rendered) keeps re-render
// churn at zero — only attribute writes on flip. Switches are user-clickable;
// clicks use a faster 0.18s transition for instant feedback.
function HeroLattice() {
  const gRef = useRef(null);

  useEffect(() => {
    const g = gRef.current;
    if (!g) return;
    while (g.firstChild) g.removeChild(g.firstChild); // guard against StrictMode double-mount

    const SVG_NS = 'http://www.w3.org/2000/svg';
    const W = 1200, H = 540, cell = 64, sw = 32, sh = 16;
    const cols = Math.ceil(W / cell), rows = Math.ceil(H / cell);
    const switches = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cx = c * cell + cell / 2;
        const cy = r * cell + cell / 2;
        const on = Math.random() < 0.13;
        const x = cx - sw / 2, y = cy - sh / 2;

        const pill = document.createElementNS(SVG_NS, 'rect');
        pill.setAttribute('x', x);
        pill.setAttribute('y', y);
        pill.setAttribute('width', sw);
        pill.setAttribute('height', sh);
        pill.setAttribute('rx', sh / 2);
        pill.dataset.idx = switches.length;
        if (on) pill.classList.add('on');
        g.appendChild(pill);

        const k = document.createElementNS(SVG_NS, 'circle');
        k.setAttribute('cx', on ? x + sw - sh / 2 : x + sh / 2);
        k.setAttribute('cy', cy);
        k.setAttribute('r', sh / 2 - 0.8);
        k.setAttribute('fill', 'url(#hero-lattice-knob-g)');
        if (on) k.classList.add('on');
        g.appendChild(k);

        switches.push({ pill, knob: k, x, sw, sh });
      }
    }

    const flipSwitch = (s, fast) => {
      if (fast) {
        s.pill.classList.add('fast');
        s.knob.classList.add('fast');
      }
      const wasOn = s.pill.classList.contains('on');
      s.pill.classList.toggle('on');
      s.knob.classList.toggle('on');
      s.knob.setAttribute('cx', wasOn ? s.x + s.sh / 2 : s.x + s.sw - s.sh / 2);
      if (fast) {
        setTimeout(() => {
          s.pill.classList.remove('fast');
          s.knob.classList.remove('fast');
        }, 260);
      }
    };

    // Top-right master switch — the visible top-right cell bulk-toggles the
    // whole lattice. We pick by rendered rect (not idx = cols - 1) because
    // preserveAspectRatio="xMidYMid slice" can crop the literal grid corners
    // depending on hero aspect ratio.
    let masterIdx = cols - 1;
    const computeMasterIdx = () => {
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // .hero-artifact has `pointer-events: auto` and on desktop covers the
      // right half of the hero — clicks there hit the card stack, never the
      // lattice. Exclude any switch whose rect overlaps it.
      const artifact = document.querySelector('.hero-artifact');
      const ar = artifact ? artifact.getBoundingClientRect() : null;
      let bestScore = -Infinity;
      let best = cols - 1;
      for (let i = 0; i < switches.length; i++) {
        const r = switches[i].pill.getBoundingClientRect();
        if (r.width === 0) continue;
        if (r.right < 0 || r.left > vw || r.bottom < 0 || r.top > vh) continue;
        if (ar && r.right > ar.left && r.left < ar.right &&
            r.bottom > ar.top && r.top < ar.bottom) continue;
        const score = r.right - r.top; // high right + low top wins
        if (score > bestScore) { bestScore = score; best = i; }
      }
      return best;
    };
    masterIdx = computeMasterIdx();

    // Reliable hit-zone for the master switch. The visible pill is only sw×sh
    // SVG units (rounded corners + pointer-events:visiblePainted shrink the hit
    // region further), so users routinely miss it. This invisible rect covers
    // the master's full grid cell — any click in that ~cell-sized area in the
    // top-right corner routes to bulkToggle via data-idx.
    const masterHitZone = document.createElementNS(SVG_NS, 'rect');
    masterHitZone.setAttribute('fill', 'transparent');
    masterHitZone.setAttribute('stroke', 'none');
    masterHitZone.setAttribute('pointer-events', 'all');
    g.appendChild(masterHitZone);
    const positionMasterHitZone = () => {
      const c = masterIdx % cols;
      const r = Math.floor(masterIdx / cols);
      masterHitZone.setAttribute('x', c * cell);
      masterHitZone.setAttribute('y', r * cell);
      masterHitZone.setAttribute('width', cell);
      masterHitZone.setAttribute('height', cell);
      masterHitZone.dataset.idx = masterIdx;
    };
    positionMasterHitZone();

    let resizeRaf = 0;
    const onResize = () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        masterIdx = computeMasterIdx();
        positionMasterHitZone();
      });
    };
    window.addEventListener('resize', onResize);

    // Suppress the ambient auto-flip briefly after a bulk-toggle. Without this,
    // the ~200ms random flipper steals stragglers from the user's "all on/off"
    // command within the first 1–2s after the click.
    let suppressFlipsUntil = 0;

    const bulkToggle = () => {
      suppressFlipsUntil = Date.now() + 1500;
      const masterWasOn = switches[masterIdx].pill.classList.contains('on');
      const targetOn = !masterWasOn;
      for (const s of switches) {
        const isOn = s.pill.classList.contains('on');
        if (isOn === targetOn) continue;
        s.pill.classList.add('fast'); s.knob.classList.add('fast');
        s.pill.classList.toggle('on');
        s.knob.classList.toggle('on');
        s.knob.setAttribute('cx', isOn ? s.x + s.sh / 2 : s.x + s.sw - s.sh / 2);
      }
      // Single cleanup timer instead of one per switch.
      setTimeout(() => {
        for (const s of switches) {
          s.pill.classList.remove('fast');
          s.knob.classList.remove('fast');
        }
      }, 260);
    };

    const onClick = (e) => {
      const t = e.target;
      if (!(t instanceof SVGRectElement)) return;
      const idx = +t.dataset.idx;
      if (Number.isNaN(idx) || !switches[idx]) return;
      if (idx === masterIdx) { bulkToggle(); return; }
      flipSwitch(switches[idx], true);
    };
    g.addEventListener('click', onClick);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return () => {
        g.removeEventListener('click', onClick);
        window.removeEventListener('resize', onResize);
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
      };
    }

    let cancelled = false;
    let timeoutId = null;
    const flip = () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible' && Date.now() >= suppressFlipsUntil) {
        flipSwitch(switches[Math.floor(Math.random() * switches.length)], false);
      }
      timeoutId = setTimeout(flip, 175 + Math.random() * 100);
    };
    timeoutId = setTimeout(flip, 600);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      g.removeEventListener('click', onClick);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <div className="hero-lattice" aria-hidden="true">
      <svg viewBox="0 0 1200 540" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="hero-lattice-knob-g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fff" />
            <stop offset="1" stopColor="#e8e4dc" />
          </linearGradient>
        </defs>
        <g ref={gRef} />
      </svg>
    </div>);

}

/* AnimatedDigits — count-up from 0 → target over durationMs (easeOutCubic).
   Fires once via IntersectionObserver when the node enters the viewport
   (threshold 0.3). Honors prefers-reduced-motion by rendering the final
   value immediately and skipping the RAF loop. SSR/initial markup renders
   the final formatted value so SEO crawlers see the correct number; the
   useEffect resets it to 0 in motion-allowed browsers, then animates up. */
function AnimatedDigits({ target, locale = 'es-ES', durationMs = 1100 }) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const fmt = new Intl.NumberFormat(locale, { useGrouping: 'always' });
    const reduce = typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      node.textContent = fmt.format(target);
      return;
    }
    let started = false;
    let rafId = 0;
    const startAnim = () => {
      const t0 = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - t0) / durationMs);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        node.textContent = fmt.format(Math.round(target * eased));
        if (t < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };
    // Reset to 0 visually, then animate up when intersected.
    node.textContent = fmt.format(0);
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !started) {
        started = true;
        startAnim();
        io.disconnect();
      }
    }, { threshold: 0.3 });
    io.observe(node);
    return () => { io.disconnect(); cancelAnimationFrame(rafId); };
  }, [target, locale, durationMs]);
  return (
    <span
      ref={ref}
      className="hero-value-digits"
      aria-label={new Intl.NumberFormat(locale, { useGrouping: 'always' }).format(target)}
    >
      {new Intl.NumberFormat(locale, { useGrouping: 'always' }).format(target)}
    </span>
  );
}

/* renderH1WithValue — derive the amber-highlighted value span at render time
   WITHOUT mutating the copy.h1 string. The h1 string stays byte-identical to
   the V1.2 lock; this helper splits it around the dialect-specific value
   pattern and wraps the middle slice in <span className="hero-value">. The
   inner digit substring `1.200` is delegated to <AnimatedDigits> for the
   count-up effect. NBSP characters inside the value cluster are preserved
   verbatim in the reconstructed output.
   If the pattern isn't found (e.g. copy was changed in the future), it falls
   back to rendering the raw string — never throws. */
function renderH1WithValue(h1Str, dialect) {
  const isRio = dialect === 'rioplatense';
  // NBSP-aware patterns. Both production copy.h1 strings use U+00A0
  // (non-breaking space) inside the value cluster.
  const valuePattern = isRio ? '+USD 1.200/mes' : '+1.200 €/mes';
  const idx = h1Str.indexOf(valuePattern);
  if (idx === -1) return h1Str;
  const before = h1Str.slice(0, idx);
  const after = h1Str.slice(idx + valuePattern.length);
  // Split the value cluster around the "1.200" digit substring so we can
  // animate the digits without touching the surrounding currency glyphs.
  // castellano: '+1.200 €/mes'  →  prefix '+', digits '1.200', suffix ' €/mes'
  // rioplatense: '+USD 1.200/mes' →  prefix '+USD ', digits '1.200', suffix '/mes'
  const valueBefore = isRio ? '+USD ' : '+';
  const valueAfter  = isRio ? '/mes'        : ' €/mes';
  return (
    <>
      {before}
      <span className="hero-value">
        {valueBefore}
        <AnimatedDigits target={1200} locale="es-ES" durationMs={1100} />
        {valueAfter}
      </span>
      {after}
    </>
  );
}

/* ---------- HERO ---------- */
function Hero({ dialect, phase, onOpenBooking }) {
  const [heroOn, setHeroOn] = useState(false);
  useEffect(() => {const t = setTimeout(() => setHeroOn(true), 600);return () => clearTimeout(t);}, []);

  const copy = {
    castellano: {
      // NBSP between `1.200` and `€/mes` keeps the number-currency unit cluster
      // together at narrow viewports — prevents `+1.200` orphaning on its own
      // line. `con IA` is short enough that a regular space is fine.
      h1: 'Recupera +1.200 €/mes con IA',
      sub: 'El Método EnAutomático identifica +1.200 €/mes que puedes recuperar con IA y te entrega el plan de 4 días para implementarlas. En 48 horas.',
      cta: 'Obtén tu Plan',
      guaranteePilot:  'Garantía · si no encontramos los +1.200 €/mes, te devolvemos los 99 €',
      guaranteeTarget: 'Garantía · si no encontramos los +1.200 €/mes, te devolvemos los 299 €'
    },
    rioplatense: {
      // NBSP between `USD` and `1.200` keeps the currency-number unit cluster
      // together at narrow viewports. `/mes` is already glyph-attached (no
      // whitespace) so no NBSP needed there.
      h1: 'Recuperá +USD 1.200/mes con IA',
      sub: 'El Método EnAutomático identifica +USD 1.200/mes que podés recuperar con IA y te entrega el plan de 4 días para implementarlas. En 48 horas.',
      cta: 'Obtené tu Plan',
      guaranteePilot:  'Garantía · si no encontramos los +USD 1.200/mes, te devolvemos los USD 99',
      guaranteeTarget: 'Garantía · si no encontramos los +USD 1.200/mes, te devolvemos los USD 299'
    }
  }[dialect];
  const guaranteeLine = phase === 'target' ? copy.guaranteeTarget : copy.guaranteePilot;

  return (
    <section id="top">
      <HeroLattice />
      <div className="wrap">
        <div className="hero-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.05fr', gap: 56, alignItems: 'center' }}>
          <div>
            <div className="hero-stamp" aria-hidden="true">
              <svg viewBox="0 0 112 112" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
                <defs>
                  <path id="hero-stamp-curve" d="M 56,56 m -44,0 a 44,44 0 1,1 88,0 a 44,44 0 1,1 -88,0" fill="none" />
                </defs>
                <circle cx="56" cy="56" r="42" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.45" />
                <circle cx="56" cy="56" r="30" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.45" />
                <text fontFamily="JetBrains Mono, ui-monospace, monospace" fontSize="8" letterSpacing="2.4" fill="currentColor" opacity="0.95">
                  <textPath href="#hero-stamp-curve" startOffset="0">DIAGNÓSTICO · CERTIFICADO · 2026 · </textPath>
                </text>
              </svg>
              <span className="hero-stamp-core serif">EA</span>
            </div>
            <h1 className="serif" style={{ fontSize: 'clamp(52px, 7.2vw, 116px)', lineHeight: 0.92, margin: 0, letterSpacing: '-0.028em', textWrap: 'balance', overflowWrap: 'break-word' }}>
              {renderH1WithValue(copy.h1, dialect)}
            </h1>

            <p style={{ maxWidth: 560, fontSize: 'clamp(17px, 1.25vw, 22px)', lineHeight: 1.5, color: 'var(--ink-2)', marginTop: 28, textWrap: 'pretty' }}>
              {copy.sub}
            </p>

            <div className="hero-cta-row" style={{ display: 'flex', gap: 12, marginTop: 36, flexWrap: 'wrap' }}>
              <button onClick={(e) => { e.preventDefault(); onOpenBooking(); }} className="btn" style={{ padding: '14px 22px', cursor: 'pointer' }}>
                {copy.cta}
              </button>
            </div>

            {phase === 'pilot' &&
              <div style={{ marginTop: 14 }}>
                <span className="tag tag-scarcity">
                  <span className="dot" />
                  <span><strong>Quedan 3 plazas gratis</strong></span>
                </span>
              </div>
            }
            <div className="mono" style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
              {guaranteeLine}
            </div>
            <div className="mono" style={{ marginTop: 4, fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
              Vista previa al instante · plan completo tras llamada con Sofía
            </div>
          </div>

          {/* Hero object — the switch artifact */}
          <HeroArtifact on={heroOn} dialect={dialect} />
        </div>
      </div>
    </section>);

}

const HERO_CASES = [
{
  id: 'EA-0042',
  name: 'Gestoría Llorens & Vidal',
  meta: 'Barcelona · 14 personas · abril 2026',
  wins: [
  ['Respuestas a clientes por WhatsApp', 'Agente de respuesta automática', '+24 h / mes'],
  ['Conciliación bancaria', 'Facturación · automatización', '+12 h / mes'],
  ['Resúmenes de llamadas', 'Transcripción automática', '+8 h / mes'],
  ['Filtro de bandeja de entrada', 'Limpieza de correo', '+4 h / mes']],
  roi: '€2.340', hours: 48
},
{
  id: 'EA-0047',
  name: 'Inmobiliaria Palermo Prop.',
  meta: 'Buenos Aires · 9 personas · abril 2026',
  wins: [
  ['Calificación de leads entrantes', 'Agente WhatsApp speed-to-lead', '+16 h / mes'],
  ['Descripciones de propiedades', 'Redacción asistida', '+8 h / mes'],
  ['Seguimiento de visitas', 'CRM · recordatorios auto', '+8 h / mes'],
  ['Publicación multi-portal', 'Sincronización automática', '+4 h / mes']],
  roi: '€1.820', hours: 36
},
{
  id: 'EA-0051',
  name: 'Bufete Carreras & Mas',
  meta: 'Sabadell · 22 personas · abril 2026',
  wins: [
  ['Resumen de expedientes', 'Lectura asistida de PDFs', '+12 h / mes'],
  ['Redacción de escritos', 'Plantillas con IA', '+8 h / mes'],
  ['Agenda de vencimientos', 'Recordatorios automáticos', '+4 h / mes'],
  ['Clasificación de correo', 'Triage por materia', '+4 h / mes']],
  roi: '€1.510', hours: 28
},
{
  id: 'EA-0055',
  name: 'Clínica Dental Rosario Centro',
  meta: 'Rosario · 6 personas · abril 2026',
  wins: [
  ['Confirmación de turnos', 'Agente WhatsApp', '+14 h / mes'],
  ['Recordatorios post-tratamiento', 'Secuencias automáticas', '+12 h / mes'],
  ['Pre-carga de historia clínica', 'Formulario pre-consulta', '+8 h / mes'],
  ['Reseñas de Google', 'Pedido post-visita', '+6 h / mes']],
  roi: '€1.280', hours: 40
}];


function HeroArtifact({ on, dialect }) {
  const [idx, setIdx] = useState(0);
  const [drag, setDrag] = useState(null);
  const ref = useRef(null);
  const go = (n) => setIdx((idx + n + HERO_CASES.length) % HERO_CASES.length);
  const onDown = (e) => {const x = e.touches ? e.touches[0].clientX : e.clientX;setDrag({ x0: x, dx: 0 });};
  const onMove = (e) => {if (!drag) return;const x = e.touches ? e.touches[0].clientX : e.clientX;setDrag({ ...drag, dx: x - drag.x0 });};
  const onUp = () => {
    if (!drag) return;
    const w = ref.current?.offsetWidth || 400;
    const threshold = Math.min(80, w * 0.18);
    if (drag.dx < -threshold) go(1);else
    if (drag.dx > threshold) go(-1);else
    if (Math.abs(drag.dx) < 5) go(1); // click-to-advance: piggyback on existing pointer wiring
    setDrag(null);
  };
  return (
    <div className="hero-artifact" style={{ position: 'relative', aspectRatio: '0.78', width: '100%' }} ref={ref}>
      {/* Backing paper */}
      <div style={{ position: 'absolute', inset: '5% 3% 3% 7%', background: 'var(--paper)', border: '1px solid var(--rule-strong)', borderRadius: 18, boxShadow: 'var(--shadow)', transform: 'rotate(-1.5deg)' }} />
      {/* Swipeable card stack */}
      <div
        className="hero-card-stack"
        style={{ position: 'absolute', inset: '2% 6% 5% 3%', cursor: drag ? 'grabbing' : 'pointer', touchAction: 'pan-y', userSelect: 'none' }}
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}>
        {HERO_CASES.map((c, i) => {
          const offset = i - idx;
          const isActive = offset === 0;
          const dragDx = isActive && drag ? drag.dx : 0;
          const baseTx = offset * 14;
          const rot = offset === 0 ? 0 : offset > 0 ? 1.2 : -1.2;
          const scale = 1 - Math.min(Math.abs(offset), 2) * 0.04;
          const z = 10 - Math.abs(offset);
          const opacity = Math.abs(offset) > 2 ? 0 : isActive ? 1 : 0.55;
          return (
            <div key={c.id}
            className="hero-case-card"
            data-active={isActive ? 'true' : 'false'}
            tabIndex={isActive ? 0 : -1}
            role={isActive ? 'button' : undefined}
            aria-label={isActive ? 'Ver siguiente diagnóstico de muestra' : undefined}
            aria-hidden={isActive ? undefined : 'true'}
            onKeyDown={isActive ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                go(1);
              }
            } : undefined}
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--paper)', border: '1px solid var(--rule-strong)', borderRadius: 18, boxShadow: 'var(--shadow)',
              padding: '40px 36px', fontFamily: 'var(--sans)',
              display: 'flex', flexDirection: 'column',
              transform: `translateX(calc(${baseTx}px + ${dragDx}px)) rotate(${rot + (isActive ? dragDx * 0.02 : 0)}deg) scale(${scale})`,
              transition: drag ? 'none' : 'transform .45s cubic-bezier(.7,0,.3,1), opacity .35s ease',
              zIndex: z, opacity, pointerEvents: isActive ? 'auto' : 'none'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
                <span className="allcaps" style={{ color: 'var(--ink-3)' }}>Diagnóstico · muestra</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>#{c.id}</span>
              </div>
              <div className="hairline-b" style={{ paddingBottom: 20, marginBottom: 24 }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 30, lineHeight: 1.1, letterSpacing: '-0.015em' }}>{c.name}</div>
                <div style={{ fontSize: 14, color: 'var(--ink-3)', marginTop: 6 }}>{c.meta}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Victorias rápidas detectadas</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {c.wins.map(([dolor, tool, horas], j) =>
                <div key={j} style={{ display: 'grid', gridTemplateColumns: '24px 1fr auto auto', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: j < 3 ? '1px dashed var(--rule)' : 'none' }}>
                    <Switch on={on && isActive} size={0.3} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{dolor}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{tool}</div>
                    </div>
                    <div className="mono" style={{ fontSize: 12, color: 'var(--on-ink)', fontWeight: 500 }}>{horas}</div>
                  </div>
                )}
              </div>
              <div className="hero-card-roi" style={{ marginTop: 'auto', padding: '20px 22px', background: 'var(--bg)', border: '1px solid var(--rule)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>ROI mensual neto</div>
                  <div className="serif" style={{ fontSize: 36, letterSpacing: '-0.02em', marginTop: 4 }}>{c.roi}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Horas / mes</div>
                  <div className="serif" style={{ fontSize: 36, letterSpacing: '-0.02em', marginTop: 4, color: on && isActive ? 'var(--on-ink)' : 'var(--ink)' }}>{on && isActive ? c.hours : 0}</div>
                </div>
              </div>
              <div style={{ position: 'absolute', right: -14, top: -14, background: 'var(--ink)', color: 'var(--bg)', padding: '8px 14px', borderRadius: 999, fontSize: 11, fontFamily: 'var(--mono)', transform: 'rotate(3deg)' }}>
                48 h
              </div>
            </div>);

        })}
      </div>
      {/* Dots-only stepper — 260502-gbs: re-add clickable dots (no counter span). Card click-to-advance + Enter/Space still work.
         zIndex: 20 ensures the pill stays above the cards (cards use inline zIndex 10-|offset|, max 10). */}
      <div className="hero-card-dots" style={{ position: 'absolute', left: '50%', bottom: -22, transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'var(--paper)', border: '1px solid var(--rule)', borderRadius: 999, boxShadow: 'var(--shadow)', zIndex: 20 }}>
        {HERO_CASES.map((_, i) =>
        <button
          key={i}
          onClick={(e) => { e.stopPropagation(); setIdx(i); }}
          aria-label={`Ir a diagnóstico ${i + 1}`}
          data-active={i === idx ? 'true' : 'false'}
          style={{
            width: 8, height: 8, borderRadius: '50%', border: 'none', padding: 0,
            background: i === idx ? 'var(--ink)' : 'var(--rule-strong)',
            cursor: 'pointer', transition: 'background .2s ease'
          }} />
        )}
      </div>
    </div>);

}

/* ---------- SAMPLE REPORT ---------- */
/* Mini-previews of each bosch-roca slide — SampleReport itself now embeds the
   live deck, so this stays here only to keep the historical thumb component. */
function SlidePreview({ n }) {
  if (n === 1) {
    return (
      <div style={{ background: 'var(--ink)', color: 'var(--bg)', borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="mono" style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'oklch(0.65 0.01 80)' }}>Preparado para</div>
        <div className="serif" style={{ fontSize: 22, lineHeight: 1, letterSpacing: '-0.02em' }}>Bosch &amp; Roca</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--mono)', fontSize: 10, color: 'oklch(0.78 0.01 80)' }}>
          <span style={{ color: 'var(--on)' }}>+60 h/mes</span>
          <span>€1.672/mes</span>
        </div>
      </div>);
  }
  if (n === 2) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <div className="mono" style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>Dolor</div>
          <div style={{ fontSize: 11, lineHeight: 1.35, color: 'var(--ink-2)' }}>Declaraciones trimestrales de impuestos = 2 semanas.</div>
        </div>
        <div>
          <div className="mono" style={{ fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 4 }}>Oportunidad</div>
          <div className="serif" style={{ fontSize: 30, color: 'var(--on-ink)', lineHeight: 0.95, letterSpacing: '-0.02em' }}>60h</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-3)', marginTop: 2 }}>recuperables/mes</div>
        </div>
      </div>);
  }
  if (n === 3) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 4, height: 96, position: 'relative' }}>
        <div style={{ background: 'var(--on)', color: 'var(--on-ink)', borderRadius: 4, padding: '6px 8px', fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>★ Quick wins</div>
        <div style={{ background: 'var(--rule)', borderRadius: 4 }} />
        <div style={{ background: 'var(--rule)', borderRadius: 4 }} />
        <div style={{ background: 'var(--rule)', borderRadius: 4, opacity: 0.5 }} />
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: -16, width: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="mono" style={{ fontSize: 8, color: 'var(--ink-3)', letterSpacing: '0.1em', whiteSpace: 'nowrap', transform: 'rotate(-90deg)', transformOrigin: 'center center' }}>IMPACTO →</span>
        </div>
        <div className="mono" style={{ position: 'absolute', bottom: -14, right: 0, fontSize: 8, color: 'var(--ink-3)', letterSpacing: '0.1em' }}>ESFUERZO →</div>
      </div>);
  }
  if (n === 4) {
    const rows = [
      ['360dialog + Claude', '€69'],
      ['Klippa + Make', '€59'],
      ['Make', 'incluido'],
      ['Cal.com + Notion', '€0']
    ];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {rows.map(([tool, cost], i) =>
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < rows.length - 1 ? '1px dashed var(--rule)' : 'none', fontSize: 11 }}>
          <span style={{ color: 'var(--ink-2)' }}>{tool}</span>
          <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 10 }}>{cost}</span>
        </div>
        )}
      </div>);
  }
  if (n === 5) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        {[1, 2, 3, 4].map((d) =>
        <div key={d} style={{ aspectRatio: '0.78', border: '1px solid var(--rule-strong)', borderRadius: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: 'var(--paper)' }}>
            <div style={{ width: '100%', background: 'var(--ink)', color: 'var(--bg)', fontFamily: 'var(--mono)', fontSize: 7, letterSpacing: '0.12em', padding: '2px 0', textAlign: 'center' }}>DÍA</div>
            <span className="serif" style={{ fontSize: 22, lineHeight: 1, marginTop: 4 }}>{d}</span>
          </div>
        )}
      </div>);
  }
  if (n === 6) {
    const futures = ['VeriFactu 2027', 'Time-tracking', 'Dashboard Looker'];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {futures.map((t, i) =>
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'var(--ink-2)' }}>
          <Switch on size={0.2} />
          <span>{t}</span>
        </div>
        )}
      </div>);
  }
  if (n === 7) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20 }}>
          <div>
            <div className="serif" style={{ fontSize: 32, color: 'var(--on-ink)', lineHeight: 0.95, letterSpacing: '-0.02em' }}>60h</div>
            <div className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', marginTop: 2, letterSpacing: '0.1em' }}>RECUPERADAS</div>
          </div>
          <div>
            <div className="serif" style={{ fontSize: 28, lineHeight: 0.95, letterSpacing: '-0.02em' }}>€1.672</div>
            <div className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', marginTop: 2, letterSpacing: '0.1em' }}>ROI / MES</div>
          </div>
        </div>
        <div className="mono" style={{ fontSize: 9, color: 'var(--ink-3)', marginTop: 10, letterSpacing: '0.06em' }}>– €128/mes en herramientas</div>
      </div>);
  }
  if (n === 8) {
    const steps = [
      'Implementar Quick Wins · 4 días',
      'Llamada de revisión · 30 min'
    ];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {steps.map((s, i) =>
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'var(--ink-2)' }}>
          <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--ink)', color: 'var(--bg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: 10, flexShrink: 0 }}>{i + 1}</span>
          <span>{s}</span>
        </div>
        )}
      </div>);
  }
  return null;
}

/* ---------- TILT CARD (plain-JSX adaptation of 21st.dev/r/tom_ui/tilt-card) ---------- */
function TiltCard({
  tiltLimit = 15,
  scale = 1.05,
  perspective = 1200,
  effect = 'evade',
  spotlight = true,
  spotlightColor = 'rgba(255,255,255,0.15)',
  className,
  style,
  children
}) {
  const cardRef = useRef(null);
  const [transform, setTransform] = useState(
    `perspective(${perspective}px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`
  );
  const [spotlightPos, setSpotlightPos] = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered] = useState(false);

  const dir = effect === 'evade' ? -1 : 1;
  const prefersReducedMotion = typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const handlePointerMove = useCallback((e) => {
    if (prefersReducedMotion) return;
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const xRot = (py - 0.5) * (tiltLimit * 2) * dir;
    const yRot = (px - 0.5) * -(tiltLimit * 2) * dir;
    setTransform(
      `perspective(${perspective}px) rotateX(${xRot}deg) rotateY(${yRot}deg) scale3d(${scale}, ${scale}, ${scale})`
    );
    if (spotlight) setSpotlightPos({ x: px * 100, y: py * 100 });
  }, [tiltLimit, scale, perspective, dir, spotlight, prefersReducedMotion]);

  const handlePointerEnter = useCallback(() => {
    if (prefersReducedMotion) return;
    setIsHovered(true);
  }, [prefersReducedMotion]);

  const handlePointerLeave = useCallback(() => {
    setTransform(
      `perspective(${perspective}px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`
    );
    setIsHovered(false);
  }, [perspective]);

  return (
    <div
      ref={cardRef}
      onPointerEnter={handlePointerEnter}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      className={className}
      style={{
        transform,
        transition: 'transform 0.2s ease-out',
        transformStyle: 'preserve-3d',
        position: 'relative',
        overflow: 'hidden',
        willChange: 'transform',
        ...style
      }}>
      {children}
      {spotlight &&
      <div
        aria-hidden="true"
        style={{
          pointerEvents: 'none',
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          overflow: 'hidden',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.3s'
        }}>
          <div
          style={{
            position: 'absolute',
            width: '200%',
            height: '200%',
            borderRadius: '9999px',
            left: `${spotlightPos.x}%`,
            top: `${spotlightPos.y}%`,
            transform: 'translate(-50%, -50%)',
            background: `radial-gradient(circle, ${spotlightColor} 0%, transparent 40%)`
          }} />
        </div>
      }
    </div>);

}

/* ---------- INCLUYE EN TU PLAN — bonus stack (brecha #3) ---------- */
// Hormozi-style bonus stacking: 5 deliverables the prospect didn't know they
// were getting, all derived from content already produced by the pipeline (so
// zero marginal coste, max perceived-value lift). Section sits between Hero
// and Proof so the bonus stack lands while the prospect is still in the
// "what do I get?" mindset triggered by the hero promise.
function IncluyeEnTuPlan({ dialect }) {
  const copy = {
    castellano: {
      label: 'Incluye en tu Plan',
      titlePrefix: 'Cinco entregables que ',
      titleEm: 'apilamos contigo.',
      sub: 'Todo lo que recibes con tu diagnóstico — sin coste adicional. Cinco piezas operativas, no "ideas a considerar".',
      bonuses: [
        { num: '01', name: 'Plan de implementación de 4 días',                         desc: 'Calendario día a día con timeboxes, responsables y dependencias.' },
        { num: '02', name: 'Llamada de revisión 30 min con el fundador Santiago',      desc: 'La reservas cuando quieras tras leer el informe. Sin presión de calendario.' },
        { num: '03', name: 'Lista negra de proveedores',                                desc: 'Las plataformas que descartamos para tu vertical y por qué.' },
        { num: '04', name: 'Benchmark sectorial',                                       desc: 'Comparativa concreta con tu vertical y dónde te queda margen.' },
        { num: '05', name: 'Checklist GDPR + Kit Consulting',                           desc: 'Lo que el RGPD exige antes de automatizar + subsidios como el Kit Consulting (hasta €6.000).' },
      ],
    },
    rioplatense: {
      label: 'Incluye en tu Plan',
      titlePrefix: 'Cinco entregables que ',
      titleEm: 'apilamos con vos.',
      sub: 'Todo lo que recibís con tu diagnóstico — sin coste adicional. Cinco piezas operativas, no "ideas a considerar".',
      bonuses: [
        { num: '01', name: 'Plan de implementación de 4 días',                         desc: 'Calendario día a día con timeboxes, responsables y dependencias.' },
        { num: '02', name: 'Llamada de revisión 30 min con el fundador Santiago',      desc: 'La reservás cuando quieras tras leer el informe. Sin presión de calendario.' },
        { num: '03', name: 'Lista negra de proveedores',                                desc: 'Las plataformas que descartamos para tu vertical y por qué.' },
        { num: '04', name: 'Benchmark sectorial',                                       desc: 'Comparativa con tu vertical y dónde te queda margen.' },
        { num: '05', name: 'Checklist GDPR + subsidios aplicables',                    desc: 'Lo que el RGPD exige antes de automatizar + subsidios públicos aplicables.' },
      ],
    },
  }[dialect];

  return (
    <section id="incluye" style={{ background: 'var(--bg)' }}>
      <div className="wrap">
        <div className="allcaps" style={{ color: 'var(--ink-3)', marginBottom: 24 }}>{copy.label}</div>

        <div style={{ marginBottom: 56 }}>
          <h2 className="serif" style={{ fontSize: 'clamp(40px, 4.5vw, 64px)', margin: 0, lineHeight: 1, letterSpacing: '-0.02em', textWrap: 'balance', maxWidth: 880 }}>
            {copy.titlePrefix}<em>{copy.titleEm}</em>
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.55, color: 'var(--ink-2)', marginTop: 24, maxWidth: 680, textWrap: 'pretty' }}>
            {copy.sub}
          </p>
        </div>

        <div className="includes-list">
          {copy.bonuses.map((b) => (
            <div className="includes-row" key={b.num}>
              <div className="includes-num mono">{b.num}</div>
              <details className="includes-content" name="incluye">
                <summary className="includes-summary">
                  <h3 className="serif" style={{ fontSize: 24, lineHeight: 1.25, margin: 0, letterSpacing: '-0.01em', color: 'var(--ink)', textWrap: 'balance' }}>
                    {b.name}
                  </h3>
                  <svg className="includes-chevron" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                    <line x1="0.5" y1="7" x2="13.5" y2="7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                    <line x1="7" y1="0.5" x2="7" y2="13.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                </summary>
                <p style={{ fontSize: 16, lineHeight: 1.55, color: 'var(--ink-2)', marginTop: 8, maxWidth: 680, textWrap: 'pretty' }}>
                  {b.desc}
                </p>
              </details>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const DECK_URL = 'https://enautomatico-decks.vercel.app/bosch-roca-2026-04-28';
const DECK_SLIDE_COUNT = 8;

function SampleReport() {
  /* Mobile-only slide state: the touch-shield routes horizontal swipes / taps
     to slide changes in the iframe. The deck reads location.hash on initial
     page load but has no hashchange listener — so we force a fresh navigation
     by adding a unique ?s=N query param along with the #slide-N fragment.
     Each src change is treated as a real navigation by the browser → deck
     reloads → reads new hash → renders correct slide. The deck's HTML+JS is
     cached so the reload is fast (~200-400 ms) — visible as a brief flash,
     not a full white-page wait. */
  const [slide, setSlide] = useState(1);
  const slideRef = useRef(1);
  const startRef = useRef(null);
  const iframeRef = useRef(null);

  /* advance(delta, wrap) — delta is +1 or -1. wrap=true loops back to 1 after
     the last slide (used for taps). wrap=false clamps at the boundaries (used
     for swipes). slideRef is the source of truth so back-to-back taps fire
     correctly regardless of React's render scheduling — setSlide only drives
     the meta-line UI counter. */
  const advance = useCallback((delta, wrap) => {
    let next = slideRef.current + delta;
    if (wrap) {
      if (next > DECK_SLIDE_COUNT) next = 1;
      else if (next < 1) next = DECK_SLIDE_COUNT;
    } else {
      next = Math.max(1, Math.min(DECK_SLIDE_COUNT, next));
    }
    if (next === slideRef.current) return;
    slideRef.current = next;
    if (iframeRef.current) {
      iframeRef.current.src = `${DECK_URL}?s=${next}#slide-${next}`;
    }
    setSlide(next);
  }, []);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    startRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };

  const onTouchEnd = (e) => {
    const start = startRef.current;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    const dt = Date.now() - start.time;
    /* Horizontal swipe → next/prev. Threshold 40 px and dx-dominates-dy
       so a slightly diagonal vertical swipe (most real swipes are) doesn't
       accidentally trigger nav. Clamp at boundaries — no wrap on swipe. */
    if (adx > 40 && adx > ady * 1.4) {
      advance(dx < 0 ? 1 : -1, false);
    } else if (adx < 10 && ady < 10 && dt < 300) {
      /* Tap → next slide. Wrap so the deck loops endlessly under repeated taps. */
      advance(1, true);
    }
    /* Vertical swipe is handled natively by the browser via touch-action:
       pan-y on the shield — page scrolls without our involvement. */
    startRef.current = null;
  };

  return (
    <section id="ejemplo" style={{ background: 'var(--bg)' }}>
      <div className="wrap">
        <div style={{ marginBottom: 40 }}>
          <h2 className="serif" style={{ fontSize: 'clamp(40px, 4.5vw, 64px)', margin: 0, lineHeight: 1, letterSpacing: '-0.02em', textWrap: 'balance', maxWidth: 720 }}>
            El Método EnAutomático en 8 diapositivas. <em>Sin relleno corporativo.</em>
          </h2>
        </div>
      </div>

      <div className="deck-embed-host">
        <div className="deck-embed">
          <iframe
            ref={iframeRef}
            src={DECK_URL}
            title="Diagnóstico muestra · Bosch & Roca"
            loading="lazy"
            allow="fullscreen"
            scrolling="no"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: 'var(--bg)' }} />
          {/* Mobile touch-shield: transparent overlay above the iframe that
              lets vertical pans pass through to the page (touch-action: pan-y)
              while routing taps + horizontal swipes to slide nav via the
              handlers above. Hidden on desktop via CSS so the iframe stays
              fully interactive there. */}
          <div className="deck-touch-shield"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            aria-hidden="true" />
        </div>

        <div className="deck-embed-meta" style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
          <span>Muestra real · Bosch &amp; Roca · 2026 · slide {slide < 10 ? '0' + slide : slide} / 0{DECK_SLIDE_COUNT}</span>
          <a href={DECK_URL} target="_blank" rel="noopener noreferrer"
            style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink)', textDecoration: 'none', borderBottom: '1px solid var(--rule-strong)', paddingBottom: 2 }}>
            Abrir en pestaña nueva ↗
          </a>
        </div>
      </div>
    </section>);

}

/* ---------- PRICING ---------- */
function Pricing({ phase, dialect }) {
  const tiers = [
  { id: 'free', price: 'Gratis', sub: 'hasta agotar plazas', status: 'Disponible ahora', active: true },
  { id: 'pilot', price: '€99', sub: 'precio actual', status: 'Cuando ROI total entregado >€10.000', active: false },
  { id: 'target', price: '€299', sub: 'precio objetivo', status: 'Cuando ROI total entregado >€100.000', active: false }];

  const phaseLabels = ['Fase 1 · piloto gratis', 'Fase 2 · piloto', 'Fase 3 · objetivo'];
  const phaseIdx = 0;
  const [selectedIdx, setSelectedIdx] = useState(phaseIdx);
  const selectedTier = tiers[selectedIdx];

  const currency = dialect === 'rioplatense' ? 'USD' : 'EUR';
  const note = dialect === 'rioplatense' ?
  'Precios en USD (o pegados a MEP/CCL). Nunca facturamos en pesos.' :
  'Precios en EUR. B2C con IVA incluido. B2B con footer "Importes sin IVA" en el informe.';

  const ladderRef = useRef(null);
  useEffect(() => {
    const el = ladderRef.current;
    if (!el) return;
    // Respect reduced motion: jump straight to in-view state.
    if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('in-view');
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
          el.classList.add('in-view');
          io.disconnect();
          break;
        }
      }
    }, { threshold: [0, 0.25, 0.5] });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section id="precios" style={{ background: 'var(--ink)', color: 'var(--bg)' }}>
      <div className="wrap">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 48, flexWrap: 'wrap', gap: 24 }}>
          <h2 className="serif" style={{ fontSize: 'clamp(40px, 4.5vw, 64px)', margin: 0, lineHeight: 1, letterSpacing: '-0.02em', maxWidth: 720 }}>
            Subimos el precio conforme entregamos. <em>Transparente.</em>
          </h2>
        </div>

        {/* Stepped ladder — barras = ROI total estimado en esa fase */}
        <div style={{ position: 'relative', paddingTop: 40, paddingBottom: 20 }}>
          <div ref={ladderRef} className="pricing-ladder-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, alignItems: 'end', maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
            {tiers.map((t, i) => {
              const heights = [40, 160, 280];
              const active = t.active;
              return (
                <div key={t.id} className={'pricing-tier' + (selectedIdx === i ? ' tier-selected' : '')} data-selected={selectedIdx === i ? 'true' : 'false'} onClick={() => setSelectedIdx(i)} style={{ padding: '0 24px', position: 'relative', cursor: 'pointer' }}>
                  <div className="tier-head" style={{ marginBottom: 20, textAlign: 'center' }}>
                    <div className="tier-price serif" style={{ fontSize: active ? 64 : 44, letterSpacing: '-0.02em', lineHeight: 1, color: active ? 'var(--on)' : 'var(--bg)', display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 8 }}>
                      {t.price}
                      {active && <Switch on={true} size={0.35} />}
                    </div>
                    <div className="tier-sub" style={{ fontSize: 13, color: 'oklch(0.75 0.01 60)', marginTop: 8 }}>{t.sub}</div>
                  </div>
                  <div className="tier-bar" style={{ height: heights[i], background: active ? 'var(--on)' : 'oklch(0.3 0.01 60)', borderRadius: '6px 6px 0 0', position: 'relative', opacity: active ? 1 : 0.55 }} />
                  <div className="tier-status" style={{ marginTop: 12, minHeight: 28, fontSize: 11, fontFamily: 'var(--mono)', color: active ? 'var(--on)' : 'oklch(0.6 0.01 60)', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center' }}>
                    {t.status}
                  </div>
                </div>);

            })}
          </div>
        </div>

        {/* Mobile-only detail panel — shows the selected tier prose */}
        <div className="pricing-mobile-detail">
          <div className="pricing-mobile-detail-label">{phaseLabels[selectedIdx]}</div>
          <div className="pricing-mobile-detail-price serif">
            {selectedTier.price}
            {selectedTier.active && <span style={{ marginLeft: 10, verticalAlign: 'middle' }}><Switch on={true} size={0.32} /></span>}
          </div>
          <div className="pricing-mobile-detail-sub">{selectedTier.sub}</div>
          <div className="pricing-mobile-detail-status">{selectedTier.status}</div>
        </div>

        <div className="pricing-info-grid" style={{ marginTop: 64, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32, borderTop: '1px solid oklch(0.3 0.01 60)', paddingTop: 32 }}>
          {[
          ['Sin contratos', 'Pagas una vez, recibes el informe. No hay suscripción oculta.'],
          ['Sin letra pequeña', 'El alcance es fijo: 8 diapositivas, 4 victorias, 1 llamada de repaso.'],
          ['Te ayudamos', 'Si quieres que implementemos algo, hablamos. Si no, tienes el plan igual.']].
          map(([t, d]) =>
          <div key={t}>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 22, marginBottom: 8 }}>{t}</div>
              <div style={{ fontSize: 14, color: 'oklch(0.75 0.01 60)', lineHeight: 1.55 }}>{d}</div>
            </div>
          )}
        </div>
      </div>
    </section>);

}

/* ---------- PRODUCTOS BENTO (5-flagship visual catalog, 260502-geo) ---------- */
// Replaces ProductosEstrella. Adapts the 21st.dev features-8 bento pattern to
// vanilla CSS + inline SVG. ZERO prices, ZERO testimonials, ZERO client logos.
// Layout: 6-col grid, 3 col-2 cards on row 1, 2 col-3 cards on row 2.
// Card order mirrors the demo (left-center-right, then left-right) and our
// CLAUDE.md priority: Facturas → Sofía → Reactiva → Presupuestos → Cerebro.
// Sofía gets the primary 'Ver demo' CTA. Other 4 carry 'Próximamente' + a
// small 'Reserva una llamada' link → cal.com/sborzone/revision.
function ProductosBento() {
  const sectionRef = useRef(null);
  const bentoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [hoverKey, setHoverKey] = useState({});

  const prefersReducedMotion = typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // One-shot IntersectionObserver — adds .flagship-bento--in-view on first hit.
  useEffect(() => {
    const el = bentoRef.current;
    if (!el) return;
    if (prefersReducedMotion) {
      setInView(true);
      el.classList.add('flagship-bento--in-view');
      return;
    }
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setInView(true);
      el.classList.add('flagship-bento--in-view');
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > 0.2) {
          setInView(true);
          el.classList.add('flagship-bento--in-view');
          io.disconnect();
          break;
        }
      }
    }, { threshold: [0, 0.2, 0.4] });
    io.observe(el);
    return () => io.disconnect();
  }, [prefersReducedMotion]);

  // Hover-replay setter (bumps a per-card counter to retrigger animations).
  const replayCard = (idx) => setHoverKey((k) => ({ ...k, [idx]: (k[idx] || 0) + 1 }));

  // Card 4 — MM:SS countdown 05:00 → 00:00, looping every 5s (or 2s on hover).
  const [countdownMs, setCountdownMs] = useState(5 * 60 * 1000);
  useEffect(() => {
    if (!inView || prefersReducedMotion) return;
    const speedMultiplier = (hoverKey[4] || 0) % 2 === 1 ? 2.5 : 1;
    const realDuration = 5000 / speedMultiplier;
    const fakeMinutesPerSec = (5 * 60 * 1000) / realDuration;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      const cycle = elapsed % realDuration;
      const remainingMs = (5 * 60 * 1000) - (cycle * fakeMinutesPerSec);
      setCountdownMs(Math.max(0, remainingMs));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, prefersReducedMotion, hoverKey[4]]);

  const formatMMSS = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const mm = String(Math.floor(totalSec / 60)).padStart(2, '0');
    const ss = String(totalSec % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  return (
    <section id="productos" ref={sectionRef}>
      <div className="wrap">
        <div className="allcaps" style={{ color: 'var(--ink-3)', marginBottom: 24 }}>Productos estrella</div>

        <div style={{ marginBottom: 56 }}>
          <h2 className="serif" style={{ fontSize: 'clamp(40px, 4.5vw, 64px)', margin: 0, lineHeight: 1, letterSpacing: '-0.02em', textWrap: 'balance', maxWidth: 880 }}>
            Si tu diagnóstico encaja, <em>esto es lo que construimos.</em>
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.55, color: 'var(--ink-2)', marginTop: 24, maxWidth: 680, textWrap: 'pretty' }}>
            Cinco productos cerrados de 2 semanas que llevamos a producción tras el diagnóstico. Sin proyectos abiertos, sin sustos de scope, sin facturas sorpresa. Cada uno con un perfil de cliente concreto.
          </p>
        </div>

        <div className="flagship-bento" ref={bentoRef}>

          {/* ─── Card 1 · Facturas Auto (col-2, top-left) ─── */}
          <div className="flagship-bento--col2" onMouseEnter={() => replayCard(1)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
              <div className="allcaps" style={{ color: 'var(--ink-3)' }}>Facturas Auto</div>
              <h3 className="serif" style={{ fontSize: 22, lineHeight: 1.25, margin: 0, letterSpacing: '-0.01em', color: 'var(--ink)', textWrap: 'balance' }}>
                VeriFactu sin tocar el teclado.
              </h3>

              {/* 3-step animated email→invoice→cloud cascade (replaces FileLock) */}
              <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0 8px' }}>
                <div style={{ position: 'relative', display: 'flex', margin: '0 auto', width: 200, height: 128, borderRadius: 16, border: '1px solid var(--rule)', alignItems: 'center', justifyContent: 'center', padding: '0 16px' }}>
                  <div style={{ position: 'absolute', inset: -8, borderRadius: 18, border: '1px solid var(--rule)', opacity: 0.5 }}/>
                  <div className="flagship-bento-step-flow" key={hoverKey[1] || 0} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="flagship-step" data-step="1" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24, color: 'var(--ink-2)', display: 'block' }}>
                        <rect x="3" y="5" width="18" height="14" rx="2"/>
                        <polyline points="3 7 12 13 21 7"/>
                      </svg>
                    </span>
                    <span className="flagship-step-arrow" data-step="1" aria-hidden="true">
                      <svg viewBox="0 0 32 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 12, color: 'var(--ink-3)', display: 'block' }}>
                        <line x1="2" y1="6" x2="26" y2="6" strokeDasharray="3 3"/>
                        <polyline points="22 2 26 6 22 10"/>
                      </svg>
                    </span>
                    <span className="flagship-step" data-step="2" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24, color: 'var(--ink-2)', display: 'block' }}>
                        <rect x="4" y="3" width="16" height="18" rx="1.5"/>
                        <line x1="8" y1="8" x2="16" y2="8"/>
                        <line x1="8" y1="12" x2="16" y2="12"/>
                        <line x1="8" y1="16" x2="13" y2="16"/>
                        <circle cx="17.5" cy="16.5" r="1.2" fill="currentColor" style={{ color: 'var(--on)' }}/>
                      </svg>
                    </span>
                    <span className="flagship-step-arrow" data-step="2" aria-hidden="true">
                      <svg viewBox="0 0 32 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 12, color: 'var(--ink-3)', display: 'block' }}>
                        <line x1="2" y1="6" x2="26" y2="6" strokeDasharray="3 3"/>
                        <polyline points="22 2 26 6 22 10"/>
                      </svg>
                    </span>
                    <span className="flagship-step" data-step="3" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24, color: 'var(--ink-2)', display: 'block' }}>
                        <path d="M17 18h-9a4 4 0 0 1 0-8 6 6 0 0 1 11.5 2A3.5 3.5 0 0 1 17 18z"/>
                        <polyline points="9 13 11 15 15 11" style={{ color: 'var(--on)' }} stroke="currentColor"/>
                      </svg>
                    </span>
                  </div>
                </div>
              </div>

              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', flex: 1, margin: 0 }}>
                Del email del proveedor a Holded/Sage en menos de 2 minutos.
              </p>
              <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="tag">Próximamente</span>
                <a href="https://cal.com/sborzone/revision" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--ink-2)', textDecoration: 'underline', textUnderlineOffset: 3 }}>Reserva una llamada</a>
              </div>
            </div>
          </div>

          {/* ─── Card 2 · Sofía Recepcionista (col-2, top-center, hero) ─── */}
          <div className="flagship-bento--col2" onMouseEnter={() => replayCard(2)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
              <div className="allcaps" style={{ color: 'var(--ink-3)' }}>Sofía Recepcionista</div>
              <h3 className="serif" style={{ fontSize: 22, lineHeight: 1.25, margin: 0, letterSpacing: '-0.01em', color: 'var(--ink)', textWrap: 'balance' }}>
                Atiende todas las llamadas. Sin guardia.
              </h3>

              {/* Phone handset + 3 sound-wave arcs (replaces 24/7 + squiggly ring) */}
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '24px 0 8px' }}>
                <div className="flagship-phone-stage" style={{ position: 'relative', width: 96, height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {/* Sound-wave arcs (right of handset) — 3 staggered, animate via flagship-phone-wave */}
                  <span className="flagship-phone-wave" data-i="1" aria-hidden="true"/>
                  <span className="flagship-phone-wave" data-i="2" aria-hidden="true"/>
                  <span className="flagship-phone-wave" data-i="3" aria-hidden="true"/>
                  {/* Handset (lucide Phone) */}
                  <svg className="flagship-handset" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 40, height: 40, color: 'var(--ink)', position: 'relative', zIndex: 1 }} aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <span className="allcaps" style={{ color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 11 }}>24/7</span>
              </div>

              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', flex: 1, margin: 0 }}>
                Un agente de voz que atiende en castellano, cualifica al interlocutor y agenda directamente en tu calendario. Para clínicas, talleres, despachos e inmobiliarias que pierden 15–25 llamadas a la semana.
              </p>
              <div style={{ marginTop: 'auto', paddingTop: 8 }}>
                <button type="button" className="btn" onClick={() => navigateTo('/sofia-demo')} style={{ fontSize: 14 }}>
                  Ver demo
                </button>
              </div>
            </div>
          </div>

          {/* ─── Card 3 · Reactiva (col-2, top-right) ─── */}
          <div className="flagship-bento--col2" onMouseEnter={() => replayCard(3)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
              <div className="allcaps" style={{ color: 'var(--ink-3)' }}>Reactiva</div>
              <h3 className="serif" style={{ fontSize: 22, lineHeight: 1.25, margin: 0, letterSpacing: '-0.01em', color: 'var(--ink)', textWrap: 'balance' }}>
                Despierta tu CRM dormido.
              </h3>

              {/* WhatsApp-style chat mockup — outgoing reactivation message */}
              <div className="flagship-bento-chat-mock" key={hoverKey[3] || 0} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '16px 12px', borderRadius: 10, border: '1px solid var(--rule)', background: 'var(--bg)' }}>
                <div className="flagship-chat-recipient" style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingBottom: 8, borderBottom: '1px solid var(--rule)' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>María Llorens</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Cliente desde 2023</span>
                </div>

                {/* Typing indicator (briefly visible before bubble) */}
                <span className="flagship-typing flagship-typing--reactiva" aria-hidden="true" style={{ alignSelf: 'flex-end' }}>
                  <span className="flagship-typing-dot" data-i="1"/>
                  <span className="flagship-typing-dot" data-i="2"/>
                  <span className="flagship-typing-dot" data-i="3"/>
                </span>

                {/* Outgoing bubble (slides in after typing) */}
                <span className="flagship-reactiva-bubble" style={{ alignSelf: 'flex-end', maxWidth: '88%', padding: '8px 12px', borderRadius: 10, borderBottomRightRadius: 2, fontSize: 13, lineHeight: 1.45, color: 'var(--on-ink)', background: 'oklch(0.72 0.15 145 / 0.14)', border: '1px solid var(--on)' }}>
                  Hola María, hace 8 meses que no nos vemos. Te dejamos hueco esta semana.
                </span>

                {/* Status line: gray ✓✓ → blue ✓✓ + "Leído · 14:32" */}
                <span className="flagship-reactiva-status" style={{ alignSelf: 'flex-end', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--ink-3)' }}>
                  <span className="flagship-reactiva-ticks" aria-hidden="true" style={{ display: 'inline-flex', gap: 1, color: 'var(--ink-3)' }}>
                    <svg viewBox="0 0 16 12" width="14" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2 7 6 11 14 1"/></svg>
                    <svg viewBox="0 0 16 12" width="14" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: -6 }}><polyline points="2 7 6 11 14 1"/></svg>
                  </span>
                  <span>Leído · 14:32</span>
                </span>

                {/* María's typing indicator (left-aligned, after Leído status) */}
                <span className="flagship-typing flagship-typing--reactiva-reply" aria-hidden="true" style={{ alignSelf: 'flex-start' }}>
                  <span className="flagship-typing-dot" data-i="1"/>
                  <span className="flagship-typing-dot" data-i="2"/>
                  <span className="flagship-typing-dot" data-i="3"/>
                </span>

                {/* Incoming bubble — María's response (slides in after her typing fades out) */}
                <span className="flagship-reactiva-incoming-bubble" style={{ alignSelf: 'flex-start', maxWidth: '88%', padding: '8px 12px', borderRadius: 10, borderBottomLeftRadius: 2, fontSize: 13, lineHeight: 1.45, color: 'var(--ink)', background: 'var(--bg)', border: '1px solid var(--rule)' }}>
                  Sí, mañana a las 10 me viene bien.
                </span>
              </div>

              <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', flex: 1, margin: 0 }}>
                Contactos olvidados, reactivados en 14 días.
              </p>
              <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="tag">Próximamente</span>
                <a href="https://cal.com/sborzone/revision" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--ink-2)', textDecoration: 'underline', textUnderlineOffset: 3 }}>Reserva una llamada</a>
              </div>
            </div>
          </div>

          {/* ─── Card 4 · Presupuestos en 5 minutos (col-3, bottom-left) ─── */}
          <div className="flagship-bento--col3" onMouseEnter={() => replayCard(4)}>
            <div className="flagship-bento-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'stretch', height: '100%' }}>
              {/* Left column: countdown + 5min badge + copy + CTA */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="allcaps" style={{ color: 'var(--ink-3)' }}>Presupuestos en 5 minutos</div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', display: 'inline-flex', padding: '8px 14px', borderRadius: 12, border: '1px solid var(--rule)', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ position: 'absolute', inset: -8, borderRadius: 16, border: '1px solid var(--rule)', opacity: 0.5 }}/>
                    <span className="flagship-countdown" style={{ fontFamily: 'var(--mono)', fontSize: 36, fontWeight: 500, color: 'var(--ink)', lineHeight: 1, letterSpacing: '-0.02em' }}>{formatMMSS(countdownMs)}</span>
                  </div>
                </div>

                <h3 className="serif" style={{ fontSize: 22, lineHeight: 1.25, margin: 0, letterSpacing: '-0.01em', color: 'var(--ink)', textWrap: 'balance' }}>
                  Del WhatsApp al PDF firmable.
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', flex: 1, margin: 0 }}>
                  Del mensaje del cliente al PDF firmable en menos de 5 minutos.
                </p>
                <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span className="tag">Próximamente</span>
                  <a href="https://cal.com/sborzone/revision" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--ink-2)', textDecoration: 'underline', textUnderlineOffset: 3 }}>Reserva una llamada</a>
                </div>
              </div>

              {/* Right column: wireframe presupuesto card with row cascade */}
              <div className="flagship-presupuesto-stage" key={hoverKey[4] || 0} style={{ position: 'relative', padding: '20px 20px 20px 28px', borderTop: '1px solid var(--rule)', borderLeft: '1px solid var(--rule)', borderTopLeftRadius: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Document header */}
                <div className="flagship-presupuesto-row" data-i="0" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid var(--rule)', paddingBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', fontFamily: 'var(--serif)' }}>Presupuesto</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>#2026-058 · 02 May 2026</span>
                </div>

                {/* Cliente line */}
                <div className="flagship-presupuesto-row" data-i="1" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                  Para: <span style={{ color: 'var(--ink-3)' }}>[Cliente]</span>
                </div>

                {/* 4 line-item rows (placeholder bars, NO PRICES) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[2, 3, 4, 5].map((i) => (
                    <div key={i} className="flagship-presupuesto-row" data-i={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'block', height: 6, borderRadius: 3, background: 'var(--rule)', flex: i % 2 === 0 ? '0 0 60%' : '0 0 75%' }}/>
                      <span style={{ display: 'block', height: 6, borderRadius: 3, background: 'var(--rule)', flex: '0 0 18%', marginLeft: 'auto' }}/>
                    </div>
                  ))}
                </div>

                {/* Total bar (slightly thicker, accent tone) */}
                <div className="flagship-presupuesto-row" data-i="6" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 8, borderTop: '1px solid var(--rule)' }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total</span>
                  <span style={{ display: 'block', height: 8, borderRadius: 4, background: 'oklch(0.72 0.15 145 / 0.20)', border: '1px solid var(--on)', flex: '0 0 28%', marginLeft: 'auto' }}/>
                </div>

                {/* "Aceptar y firmar" button (pulses once at end of cascade) */}
                <button type="button" className="flagship-presupuesto-button" disabled aria-hidden="true" style={{ marginTop: 4, alignSelf: 'flex-end', padding: '6px 12px', borderRadius: 8, background: 'var(--on)', color: 'var(--on-ink)', border: '1px solid var(--on)', fontSize: 12, fontWeight: 500, cursor: 'default', fontFamily: 'var(--sans)' }}>
                  Aceptar y firmar
                </button>
              </div>
            </div>
          </div>

          {/* ─── Card 5 · Cerebro de la Empresa (col-3, bottom-right) ─── */}
          <div className="flagship-bento--col3" onMouseEnter={() => replayCard(5)}>
            <div className="flagship-bento-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'stretch', height: '100%' }}>
              {/* Left column: users icon + copy + CTA */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="allcaps" style={{ color: 'var(--ink-3)' }}>Cerebro de la Empresa</div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24, color: 'var(--ink-2)' }}>
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="8.5" cy="7" r="4"/>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>

                <h3 className="serif" style={{ fontSize: 22, lineHeight: 1.25, margin: 0, letterSpacing: '-0.01em', color: 'var(--ink)', textWrap: 'balance' }}>
                  Pregunta. Recibe. Cita la fuente.
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', flex: 1, margin: 0 }}>
                  El conocimiento de tu equipo, accesible en WhatsApp.
                </p>
                <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span className="tag">Próximamente</span>
                  <a href="https://cal.com/sborzone/revision" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--ink-2)', textDecoration: 'underline', textUnderlineOffset: 3 }}>Reserva una llamada</a>
                </div>
              </div>

              {/* Right column: realistic Q&A chat (no avatars) */}
              <div style={{ position: 'relative', paddingTop: 24, paddingBottom: 24, borderLeft: '1px solid var(--rule)', paddingLeft: 24 }}>
                <div key={hoverKey[5] || 0} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12, height: '100%' }}>
                  {/* Row 1: incoming question (left-aligned, gray bubble) */}
                  <div className="flagship-chat-row" data-row="1" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '85%', alignSelf: 'flex-start', position: 'relative' }}>
                    <span className="flagship-typing" aria-hidden="true">
                      <span className="flagship-typing-dot" data-i="1"/>
                      <span className="flagship-typing-dot" data-i="2"/>
                      <span className="flagship-typing-dot" data-i="3"/>
                    </span>
                    <span className="flagship-chat-bubble" style={{ display: 'block', borderRadius: 8, borderBottomLeftRadius: 2, border: '1px solid var(--rule)', padding: '6px 10px', fontSize: 12, lineHeight: 1.4, color: 'var(--ink)', background: 'var(--bg)' }}>
                      ¿Gustavo de SuperNova ya ha pagado la factura #058?
                    </span>
                  </div>

                  {/* Row 2: outgoing answer (right-aligned, accent bubble) */}
                  <div className="flagship-chat-row" data-row="2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, width: '85%', alignSelf: 'flex-end', position: 'relative' }}>
                    <span className="flagship-typing" aria-hidden="true">
                      <span className="flagship-typing-dot" data-i="1"/>
                      <span className="flagship-typing-dot" data-i="2"/>
                      <span className="flagship-typing-dot" data-i="3"/>
                    </span>
                    <span className="flagship-chat-bubble" style={{ display: 'block', borderRadius: 8, borderBottomRightRadius: 2, border: '1px solid var(--on)', padding: '6px 10px', fontSize: 12, lineHeight: 1.4, color: 'var(--on-ink)', background: 'oklch(0.72 0.15 145 / 0.14)' }}>
                      Aún no. Vence ayer; única impagada del cliente.
                    </span>
                  </div>

                  {/* Row 3: outgoing follow-up with arrow (right-aligned, accent bubble) */}
                  <div className="flagship-chat-row" data-row="3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, width: '85%', alignSelf: 'flex-end', position: 'relative' }}>
                    <span className="flagship-typing" aria-hidden="true">
                      <span className="flagship-typing-dot" data-i="1"/>
                      <span className="flagship-typing-dot" data-i="2"/>
                      <span className="flagship-typing-dot" data-i="3"/>
                    </span>
                    <span className="flagship-chat-bubble" style={{ display: 'block', borderRadius: 8, borderBottomRightRadius: 2, border: '1px solid var(--on)', padding: '6px 10px', fontSize: 12, lineHeight: 1.4, color: 'var(--on-ink)', background: 'oklch(0.72 0.15 145 / 0.14)' }}>
                      Te dejo borrador del recordatorio listo para enviar →
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ---------- VOICE AGENT ---------- */
function VoiceAgent({ dialect }) {
  const script = dialect === 'castellano' ? [
  ['Sofía', 'Hola, soy Sofía, la asistente de IA de EnAutomático. Voy a hacerte unas preguntas rápidas sobre tu negocio para tener tu diagnóstico en 48 horas. ¿Empezamos?'],
  ['Tú', 'Claro, adelante.'],
  ['Sofía', '¿A qué se dedica tu negocio?'],
  ['Tú', 'Tengo una gestoría en Gràcia. Somos 14 personas, llevamos impuestos y nóminas de unos 200 autónomos y pymes.'],
  ['Sofía', 'Perfecto. ¿Cuál es el mayor dolor de cabeza de tu semana?']] :
  [
  ['Sofía', 'Hola, soy Sofía de EnAutomático. Gracias por llamar. Te voy a hacer unas preguntas rápidas sobre tu negocio para identificar en qué partes la IA te puede ayudar a recuperar tiempo. ¿Arrancamos?'],
  ['Vos', 'Dale, arrancá.'],
  ['Sofía', '¿A qué se dedica tu negocio?'],
  ['Vos', 'Tengo una inmobiliaria en Palermo. Somos 9, manejamos unas 120 propiedades en alquiler y venta.'],
  ['Sofía', 'Genial. ¿Cuál es el mayor dolor de cabeza de tu semana?']];


  return (
    <section>
      <div className="wrap">
        <div className="voice-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 80, alignItems: 'center' }}>
          <div>
            <h2 className="serif" style={{ fontSize: 'clamp(36px, 4vw, 56px)', margin: 0, lineHeight: 1, letterSpacing: '-0.02em', textWrap: 'balance' }}>
              Una conversación <em>como cualquier otra.</em>
            </h2>
            <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-2)', marginTop: 16, maxWidth: 440 }}>No tienes que prepararte. No tienes que saber de IA. No hay vocabulario técnico, ni formularios, ni deberes.

            </p>
          </div>

          {/* Call mock */}
          <div className="card" style={{ padding: 0, overflow: 'hidden', background: 'var(--paper)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--rule)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--on)', boxShadow: '0 0 0 4px oklch(0.72 0.15 145 / 0.22)' }} />
                <span className="mono" style={{ fontSize: 12 }}>+34 93 — — — · en curso</span>
              </div>
              <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>04:17</span>
            </div>
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {script.map(([who, text], i) =>
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 16, alignItems: 'start' }}>
                  <div className="mono" style={{ fontSize: 11, color: who === 'Sofía' ? 'var(--on-ink)' : 'var(--ink-3)', paddingTop: 4, textTransform: 'uppercase' }}>{who}</div>
                  <div style={{ fontSize: 14.5, lineHeight: 1.55, color: who === 'Sofía' ? 'var(--ink)' : 'var(--ink-2)', fontStyle: who === 'Sofía' ? 'normal' : 'italic' }}>
                    {text}
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 16, alignItems: 'center', paddingTop: 8 }}>
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase' }}>Sofía</div>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', height: 20 }}>
                  {[...Array(22)].map((_, i) =>
                  <div key={i} style={{ width: 2, height: 4 + Math.abs(Math.sin(i * 0.7)) * 16, background: 'var(--on)', borderRadius: 2, animation: `pulse ${1 + i * 0.05}s ease-in-out infinite alternate` }} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes pulse { to { transform: scaleY(1.6); opacity: 0.7; } }`}</style>
    </section>);

}

/* ---------- COMPARE ROUTES ---------- */
function CompareRoutes({ dialect }) {
  const data = {
    castellano: [
      {
        name: 'EnAutomatico',
        hours: '~1 h',
        hoursTip: '25 min de llamada + 30 min de revisión. Sin briefings ni documentos.',
        cash: '€299 (hoy €99 piloto · gratis hasta agotar plazas)',
        cashTip: 'Pagas una vez. Si quieres implementación, se cotiza aparte.',
        time: '48 h al deck · 4 días al primer quick win',
        timeTip: 'Deck en tu correo en 48 h. Quick win en 4 días.',
        gets: '8 slides · 3–5 quick wins · plan 4 días · ROI cuantificado',
        getsTip: 'Matriz impacto-esfuerzo, herramientas con UI en español, ROI con supuestos auditables.',
        risk: 'Alcance fijo y productizado',
        riskTip: 'Lo que ves es lo que recibes. Si necesitas algo a medida, esto te da el plan, no la implementación.',
        isUs: true,
      },
      {
        name: 'Contratar consultor',
        hours: '~30 h',
        hoursTip: 'Briefings, discovery, revisiones, alineación con tu equipo. Casi siempre subestimado.',
        cash: '€3.000–€8.000 diagnóstico · €15K+ implementación',
        cashTip: 'El diagnóstico es solo el principio. La implementación sube a 5 cifras rápido.',
        time: '4–8 semanas',
        timeTip: 'Discovery, entrevistas, propuesta, revisiones. Más de un mes antes de la primera implementación.',
        gets: 'Propuesta a medida, larga, atada al consultor',
        getsTip: 'Propuesta extensa, difícil de ejecutar sin el consultor. Si rompes la relación, el conocimiento se queda con ellos.',
        risk: 'Scope creep y dependencia continua',
        riskTip: 'Cada cambio de alcance es nueva conversación contractual. La dependencia continua suele ser la norma.',
        isUs: false,
      },
      {
        name: 'Hacerlo tú',
        hours: '~80 h',
        hoursTip: 'Leer, demos, trials, comparar, abandonar, retomar. Curva real cuando no es tu día a día.',
        cash: '€100–300/mes en pruebas y suscripciones',
        cashTip: 'Trials apilados (CRM, automatizador, IA, WhatsApp). Se acumula rápido y desaparece de tu radar.',
        time: '4–12 semanas (si lo terminas)',
        timeTip: 'La mayoría queda con trials abiertos y una solución a medio montar.',
        gets: 'Media solución funcional, sin documentar',
        getsTip: 'Un Notion a medias, un par de automatizaciones, y notas que solo tú entiendes.',
        risk: 'Elegir mal las herramientas y abandonar a la mitad',
        riskTip: 'Eliges mal, lo descubres semanas después, y aguantas o empiezas de cero. Ese coste oculto infla las 80 h.',
        isUs: false,
      },
    ],
    rioplatense: [
      {
        name: 'EnAutomatico',
        hours: '~1 h',
        hoursTip: '25 min de llamada + 30 min de revisión. Sin briefings ni documentos.',
        cash: 'USD 299 (hoy USD 99 piloto · gratis hasta agotar plazas)',
        cashTip: 'Pagás una vez. Si querés implementación, se cotiza aparte.',
        time: '48 h al deck · 4 días al primer quick win',
        timeTip: 'Deck en tu correo en 48 h. Quick win en 4 días.',
        gets: '8 slides · 3–5 quick wins · plan 4 días · ROI cuantificado',
        getsTip: 'Matriz impacto-esfuerzo, herramientas con UI en español, ROI con supuestos auditables.',
        risk: 'Alcance fijo y productizado',
        riskTip: 'Lo que ves es lo que recibís. Si necesitás algo a medida, esto te da el plan, no la implementación.',
        isUs: true,
      },
      {
        name: 'Contratar consultor',
        hours: '~30 h',
        hoursTip: 'Briefings, discovery, revisiones, alineación con tu equipo. Casi siempre subestimado.',
        cash: 'USD 3.000–8.000 diagnóstico · USD 15K+ implementación',
        cashTip: 'El diagnóstico es solo el principio. La implementación sube a 5 cifras rápido.',
        time: '4–8 semanas',
        timeTip: 'Discovery, entrevistas, propuesta, revisiones. Más de un mes antes de la primera implementación.',
        gets: 'Propuesta a medida, larga, atada al consultor',
        getsTip: 'Propuesta extensa, difícil de ejecutar sin el consultor. Si rompés la relación, el conocimiento se queda con ellos.',
        risk: 'Scope creep y dependencia continua',
        riskTip: 'Cada cambio de alcance es nueva conversación contractual. La dependencia continua suele ser la norma.',
        isUs: false,
      },
      {
        name: 'Hacerlo vos',
        hours: '~80 h',
        hoursTip: 'Leer, demos, trials, comparar, abandonar, retomar. Curva real cuando no es tu día a día.',
        cash: 'USD 100–300/mes en pruebas y suscripciones',
        cashTip: 'Trials apilados (CRM, automatizador, IA, WhatsApp). Se acumula rápido y desaparece de tu radar.',
        time: '4–12 semanas (si lo terminás)',
        timeTip: 'La mayoría queda con trials abiertos y una solución a medio montar.',
        gets: 'Media solución funcional, sin documentar',
        getsTip: 'Un Notion a medias, un par de automatizaciones, y notas que solo vos entendés.',
        risk: 'Elegir mal las herramientas y abandonar a la mitad',
        riskTip: 'Elegís mal, lo descubrís semanas después, y aguantás o empezás de cero. Ese coste oculto infla las 80 h.',
        isUs: false,
      },
    ],
  };
  const routes = data[dialect] || data.castellano;
  const usIdx = routes.findIndex((r) => r.isUs);
  const [selectedIdx, setSelectedIdx] = useState(usIdx >= 0 ? usIdx : 0);
  const selected = routes[selectedIdx];
  const rows = [
    { key: 'cash', icon: '💶', label: 'Coste cash' },
    { key: 'time', icon: '📅', label: 'Hasta el primer resultado' },
    { key: 'gets', icon: '📦', label: 'Lo que recibes' },
    { key: 'risk', icon: '⚠️', label: 'Riesgo principal' },
  ];

  /* ----- Motion: Layer A (count-up + EA fade/scale), Layer B (bar fill), Layer C (per-row reveal) ----- */
  const prefersReducedMotion = typeof window !== 'undefined' &&
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const sectionRef = useRef(null);
  const rowRefs = useRef([]);
  const [played, setPlayed] = useState(prefersReducedMotion);
  const [counts, setCounts] = useState([null, 0, 0]); // index 0 = EA (no count), 1 = Consultor, 2 = DIY
  const [labelIn, setLabelIn] = useState(prefersReducedMotion);
  const [rowsRevealed, setRowsRevealed] = useState(
    prefersReducedMotion ? [true, true, true, true] : [false, false, false, false]
  );

  // Section-level intersection observer drives Layer A + B (once per page load).
  useEffect(() => {
    if (prefersReducedMotion) return;
    if (!sectionRef.current) return;
    const startAnimation = () => {
      // Layer A — EA label fade + scale immediately
      setLabelIn(true);

      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const animateCount = (idx, target, delay, duration) => {
        setTimeout(() => {
          const start = performance.now();
          const tick = (now) => {
            const elapsed = now - start;
            const t = Math.min(elapsed / duration, 1);
            const value = Math.round(target * easeOutCubic(t));
            setCounts((prev) => {
              const next = [...prev];
              next[idx] = value;
              return next;
            });
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }, delay);
      };
      animateCount(1, 30, 150, 700);
      animateCount(2, 80, 300, 700);
      // After both finish (300 + 700 = 1000ms), flip `played` so render snaps to range strings.
      setTimeout(() => setPlayed(true), 1100);
    };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          startAnimation();
          obs.disconnect();
        }
      });
    }, { threshold: 0.3 });
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-row reveal observers (Layer C) — once per row, threshold 0.4, rootMargin -10%.
  useEffect(() => {
    if (prefersReducedMotion) return;
    const observers = rowRefs.current.map((el, idx) => {
      if (!el) return null;
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setRowsRevealed((prev) => {
              if (prev[idx]) return prev;
              const next = [...prev];
              next[idx] = true;
              return next;
            });
            obs.disconnect();
          }
        });
      }, { threshold: 0.4, rootMargin: '-10% 0px' });
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o && o.disconnect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to render the hero number for a column (desktop or mobile shares this logic).
  const renderHero = (r, i) => {
    if (i === 0) {
      // EA label: fade + scale in, no count-up.
      return (
        <span
          className="col-hero-label"
          style={{
            opacity: labelIn ? 1 : 0,
            transform: labelIn ? 'scale(1)' : 'scale(0.92)',
            transition: 'opacity 200ms ease-out, transform 200ms ease-out',
            display: 'inline-block',
          }}
        >
          {r.hours}
        </span>
      );
    }
    if (played) return r.hours;
    return Math.round(counts[i] || 0) + ' h';
  };

  return (
    <section id="compara" ref={sectionRef} style={{ borderTop: 'none', background: 'var(--bg-2)' }}>
      <div className="wrap">
        <div style={{ marginBottom: 48 }}>
          <h2 className="serif" style={{ fontSize: 'clamp(40px, 4.5vw, 64px)', margin: 0, lineHeight: 1, letterSpacing: '-0.02em', textWrap: 'balance', maxWidth: 820 }}>
            Tres maneras de hacerlo. <em>Una sale a 1 hora.</em>
          </h2>
        </div>

        {/* Desktop grid */}
        <div className="compare-routes-grid">
          {/* Names row: empty rail + 3 centered column names */}
          <div key="name-rail" className="col col-rail-header" aria-hidden="true" />
          {routes.map((r, i) => (
            <div key={`name-${i}`} className={'col col-name' + (r.isUs ? ' col-us' : '')}>
              <span className="col-name-text">{r.name.toUpperCase()}</span>
              {r.isUs && <Switch on={true} size={0.35} />}
            </div>
          ))}
          {/* Tu tiempo row: rail label + hero numbers */}
          <div key="tt-rail" className="col col-tt-rail">
            <span aria-hidden="true" style={{ marginRight: 8 }}>⏱</span>Tu tiempo
          </div>
          {routes.map((r, i) => (
            <div key={`hero-${i}`} className={'col col-hero-cell' + (r.isUs ? ' col-us' : '')} data-tip={r.hoursTip}>
              <div className="col-hero">{renderHero(r, i)}</div>
            </div>
          ))}

          {/* Four labeled rows. Each row spans full width with the label, then 3 cells below. */}
          <div className="compare-routes-rows" style={{ gridColumn: '1 / -1' }}>
            {rows.map((row, rowIdx) => (
              <div
                key={row.key}
                ref={(el) => { rowRefs.current[rowIdx] = el; }}
                className={'compare-routes-row' + (rowsRevealed[rowIdx] ? ' is-revealed' : '')}
              >
                <div className="compare-routes-row-label">
                  <span aria-hidden="true" style={{ marginRight: 8 }}>{row.icon}</span>
                  {row.label}
                </div>
                <div className="compare-routes-row-cells">
                  {routes.map((r, i) => (
                    <div key={i} className={'cell' + (r.isUs ? ' cell-us' : '')} data-tip={r[row.key + 'Tip']}>
                      {r[row.key]}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile tab selector */}
        <div className="compare-routes-tabs" role="tablist" aria-label="Compara los caminos">
          {routes.map((r, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={selectedIdx === i}
              className={(selectedIdx === i ? 'active' : '') + (r.isUs ? ' tab-us' : '')}
              onClick={() => setSelectedIdx(i)}
            >
              {r.name}
            </button>
          ))}
        </div>

        {/* Mobile detail panel — shares motion state with desktop via `selectedIdx`. */}
        {/* Mobile per-row reveal intentionally omitted — section-level animation suffices. */}
        <div className={'compare-routes-mobile-detail' + (selected.isUs ? ' is-us' : '')}>
          <div className="md-label">
            <span>{selected.name.toUpperCase()}</span>
            {selected.isUs && <Switch on={true} size={0.35} />}
          </div>
          <div className="md-hero">{renderHero(selected, selectedIdx)}</div>
          {rows.map((row) => (
            <div key={row.key} className="md-row">
              <div className="md-row-label">
                <span aria-hidden="true" style={{ marginRight: 6 }}>{row.icon}</span>
                {row.label}
              </div>
              <div className="md-row-value">{selected[row.key]}</div>
            </div>
          ))}
        </div>

      </div>
    </section>);

}

/* ---------- PROOF / CASE STRIP ---------- */
function Proof() {
  const cases = [
    { city: 'Barcelona', vertical: 'Gestoría',                size: '14 pers.', hours: '48 h/mes', roi: '€2.340' },
    { city: 'Madrid',    vertical: 'Asesoría laboral',        size: '18 pers.', hours: '44 h/mes', roi: '€1.980' },
    { city: 'Valencia',  vertical: 'Inmobiliaria',            size: '11 pers.', hours: '32 h/mes', roi: '€1.660' },
    { city: 'Sevilla',   vertical: 'Clínica dental',          size: '8 pers.',  hours: '30 h/mes', roi: '€1.350' },
    { city: 'Bilbao',    vertical: 'Taller mecánico',         size: '6 pers.',  hours: '26 h/mes', roi: '€1.240' },
    { city: 'Málaga',    vertical: 'Despacho de arquitectos', size: '9 pers.',  hours: '30 h/mes', roi: '€1.650' },
    { city: 'Sabadell',  vertical: 'Bufete',                  size: '22 pers.', hours: '28 h/mes', roi: '€1.510' },
    { city: 'Zaragoza',  vertical: 'Asesoría fiscal',         size: '16 pers.', hours: '50 h/mes', roi: '€2.150' }
  ];
  // Track is duplicated 2× and animates 0 → -50%. With 8 originals × 320px + gaps,
  // each copy is ~2.7k wider than any viewport, so the visible window always shows
  // part of the doubled track — no trailing gap at any frame.
  const ticker = [...cases, ...cases];

  // Ghost wordmark layer — three counter-scrolling rows behind the cards.
  // Mix 1 (subjects + verbs + italic kickers): each row carries a
  // different content register so any vertical slice reads as a
  // sentence fragment, e.g. "TIEMPO · RECUPERADO · en automático".
  // Row A: SUBJECTS — what gets acted on (front, --lg, scrolls right).
  // Row B: VERBS — what we do to it (mid, default, scrolls left).
  // Row C: italic Spanish sub-clauses — the editorial kicker (back, --sm + --italic, scrolls right).
  const ghostSubjects = [
    'TIEMPO', 'HORAS', 'TRABAJO', 'CITAS',
    'FACTURAS', 'CORREOS', 'CONSULTAS', 'DECISIONES'
  ];
  const ghostVerbs = [
    'RECUPERADO', 'LIBERADO', 'AUTOMATIZADO', 'DELEGADO',
    'ORDENADO', 'SIMPLIFICADO', 'DIGITALIZADO', 'DESBLOQUEADO'
  ];
  const ghostKickers = [
    'en automático', 'sin esfuerzo', 'en 48 horas', 'sin reuniones',
    'en castellano', 'sin humo', 'sin teorías', 'en 4 días'
  ];
  const ghostRowA = [...ghostSubjects, ...ghostSubjects];
  const ghostRowB = [...ghostVerbs, ...ghostVerbs];
  const ghostRowC = [...ghostKickers, ...ghostKickers];

  // Cursor-warmth: radial-gradient "reading lamp" tracking the pointer.
  // RAF-throttled, off on prefers-reduced-motion.
  const sectionRef = useRef(null);
  const warmthRef = useRef(null);
  const rafRef = useRef(null);
  useEffect(() => {
    const reduced = typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    const section = sectionRef.current;
    const warmth = warmthRef.current;
    if (!section || !warmth) return;
    const onMove = (e) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const rect = section.getBoundingClientRect();
        const px = ((e.clientX - rect.left) / rect.width) * 100;
        const py = ((e.clientY - rect.top) / rect.height) * 100;
        warmth.style.background =
          `radial-gradient(38vw circle at ${px}% ${py}%, var(--bg) 0%, transparent 55%)`;
        warmth.dataset.active = 'true';
      });
    };
    const onLeave = () => { warmth.dataset.active = 'false'; };
    section.addEventListener('pointermove', onMove);
    section.addEventListener('pointerleave', onLeave);
    return () => {
      section.removeEventListener('pointermove', onMove);
      section.removeEventListener('pointerleave', onLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <section ref={sectionRef} className="proof-strip" style={{ background: 'var(--bg-2)' }} aria-label="Ejemplos de diagnóstico">
      <div className="proof-ghost-layer" aria-hidden="true">
        <div className="proof-ghost-track proof-ghost-track--a">
          {ghostRowA.map((word, i) => (
            <span key={i} className="proof-ghost-word proof-ghost-word--lg">{word}<span className="proof-ghost-dot"> · </span></span>
          ))}
        </div>
        <div className="proof-ghost-track proof-ghost-track--b">
          {ghostRowB.map((word, i) => (
            <span key={i} className="proof-ghost-word">{word}<span className="proof-ghost-dot"> · </span></span>
          ))}
        </div>
        <div className="proof-ghost-track proof-ghost-track--c">
          {ghostRowC.map((word, i) => (
            <span key={i} className="proof-ghost-word proof-ghost-word--sm proof-ghost-word--italic">{word}<span className="proof-ghost-dot"> · </span></span>
          ))}
        </div>
      </div>
      <div ref={warmthRef} className="proof-warmth" data-active="false" aria-hidden="true" />
      <div className="proof-marquee" role="region" aria-roledescription="carousel">
        <div className="proof-marquee-track">
          {ticker.map((c, i) =>
          <TiltCard
            key={i}
            className="proof-tilt-wrap"
            tiltLimit={4}
            scale={1.015}
            spotlightColor="rgba(15, 18, 25, 0.04)">
              <div
              className="proof-card proof-card-marquee"
              aria-hidden={i >= cases.length}>
                <div className="serif" style={{ fontSize: 26, color: 'var(--ink)', letterSpacing: '-0.02em', lineHeight: 1.05, marginBottom: 4 }}>{c.vertical}</div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 22 }}>{c.size}</div>
                <div style={{ borderTop: '1px solid var(--rule)', paddingTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>Recuperado</div>
                    <div className="serif" style={{ fontSize: 22, color: 'var(--ink)', letterSpacing: '-0.015em' }}>{c.hours}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>ROI/mes</div>
                    <div className="serif" style={{ fontSize: 26, color: 'var(--on-ink)', letterSpacing: '-0.015em' }}>{c.roi}</div>
                  </div>
                </div>
              </div>
            </TiltCard>
          )}
        </div>
      </div>
    </section>);

}

/* ---------- FAQ ---------- */
function FAQ() {
  const items = [
  ['¿Y si mi negocio es demasiado pequeño / grande?', 'El punto óptimo son 5–50 personas. Por debajo de 5, las victorias rápidas son demasiado pocas para justificar el informe. Por encima de 50, normalmente necesitas un proyecto de consultoría clásico — no lo que vendemos.'],
  ['¿De verdad me llama una IA?', 'Sí. Es un agente de voz hecho con Retell.ai y ElevenLabs. Está entrenado para esta entrevista específica: 20–30 minutos, preguntas de general a específico, detección de dialecto. No vende nada en la llamada.'],
  ['¿Qué datos se guardan? ¿RGPD?', 'La grabación y transcripción del dueño del negocio se procesan como parte de la entrega del diagnóstico que reservaste (base jurídica: medidas precontractuales, Art. 6(1)(b) RGPD). Sofía te anuncia explícitamente al inicio que es un asistente de IA. No grabamos a tus clientes ni a tu equipo. Si decides no continuar, Sofía cierra la llamada y no se procesa nada. El detalle completo está en /privacidad.'],
  ['¿Qué pasa si el informe no me convence?', 'En la fase piloto es gratuito. No hay nada que devolver. En la fase paga, si el informe no identifica al menos 3 victorias implementables, devolvemos el 100 %.'],
  ['¿Tenéis experiencia en mi sector?', 'Empezamos con gestorías e inmobiliarias porque son los sectores donde conocemos mejor las herramientas (Holded, Sage, A3, CRMs sectoriales). Si estás en otro sector, lo decimos antes de la llamada.'],
  ['¿Implementáis vosotros las soluciones?', 'Opcional. El informe es autosuficiente: cualquier equipo técnico puede ejecutarlo. Si prefieres que lo hagamos nosotros, el precio estrella es el agente de WhatsApp speed-to-lead (€3.500 / USD 2.500, 2 semanas).'],
  ['¿Solo español?', 'Solo español — castellano o rioplatense. La propuesta de valor depende de verificar que cada herramienta recomendada tenga UI en español y precio en EUR/USD. No diluimos el foco.'],
  ['¿Cuánto tiempo de mi equipo necesitáis?', 'Solo el del dueño. 20–30 min de llamada + 30 min de revisión. Ningún otro empleado tiene que prepararse ni estar disponible.']];

  const [open, setOpen] = useState(0);
  return (
    <section id="faq" style={{ borderTop: 'none' }}>
      <div className="wrap">
        <div className="faq-main-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 80 }}>
          <h2 className="serif" style={{ fontSize: 'clamp(36px, 4vw, 56px)', margin: 0, lineHeight: 1, letterSpacing: '-0.02em', textWrap: 'balance' }}>
            Lo que nos preguntan <em>antes de reservar.</em>
          </h2>
          <div style={{ borderTop: '1px solid var(--rule)' }}>
            {items.map(([q, a], i) =>
            <div key={i} style={{ borderBottom: '1px solid var(--rule)' }}>
                <button onClick={() => setOpen(open === i ? -1 : i)} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', padding: '24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                  <span className="serif" style={{ fontSize: 22, letterSpacing: '-0.015em', paddingRight: 24 }}>{q}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 20, color: 'var(--ink-3)', flexShrink: 0 }}>{open === i ? '−' : '+'}</span>
                </button>
                {open === i &&
              <div style={{ paddingBottom: 24, fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.6, maxWidth: 640 }}>{a}</div>
              }
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}

/* ---------- BOOK CTA ---------- */
function Book({ dialect, phase, onOpenBooking }) {
  const switchRef = useRef(null);
  const [switchOn, setSwitchOn] = useState(false);

  const prefersReducedMotion = typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // One-shot IntersectionObserver — flips the hero Switch from off → on the
  // first time it scrolls into view, echoing the central toggle motif.
  useEffect(() => {
    const el = switchRef.current;
    if (!el) return;
    if (prefersReducedMotion) { setSwitchOn(true); return; }
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setSwitchOn(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          setSwitchOn(true);
          io.disconnect();
          break;
        }
      }
    }, { threshold: [0, 0.5, 0.9] });
    io.observe(el);
    return () => io.disconnect();
  }, [prefersReducedMotion]);

  const copy = dialect === 'castellano' ? {
    title: 'Obtén tu Plan.',
    sub: 'Cuéntanos sobre tu negocio y verás al instante el detalle de los +1.200 €/mes que tu negocio puede recuperar con IA. Sin llamada, sin coste, sin compromiso.',
    microcopy: 'Plan completo tras llamada con Sofía',
    cta: 'Obtener Plan'
  } : {
    title: 'Obtené tu Plan.',
    sub: 'Contanos sobre tu negocio y vas a ver al instante el detalle de los +USD 1.200/mes que tu negocio puede recuperar con IA. Sin llamada, sin coste, sin compromiso.',
    microcopy: 'Plan completo tras llamada con Sofía',
    cta: 'Obtener Plan'
  };
  return (
    <section id="reserva" style={{ borderTop: 'none' }}>
      <div className="wrap-narrow" style={{ textAlign: 'center' }}>
        <div ref={switchRef} style={{ display: 'inline-flex', marginBottom: 32 }}>
          <Switch on={switchOn} size={1.4} animated />
        </div>
        <h2 className="serif" style={{ fontSize: 'clamp(52px, 7vw, 96px)', margin: 0, lineHeight: 0.95, letterSpacing: '-0.025em', textWrap: 'balance' }}>
          {copy.title}
        </h2>
        <p style={{ fontSize: 19, color: 'var(--ink-2)', marginTop: 24, lineHeight: 1.5, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto', textWrap: 'pretty' }}>{copy.sub}</p>
        <div className="mono" style={{ marginTop: 12, fontSize: 12, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>
          {copy.microcopy}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24, flexWrap: 'wrap' }}>
          <button onClick={(e) => { e.preventDefault(); onOpenBooking(); }} className="btn" style={{ padding: '18px 28px', fontSize: 16, cursor: 'pointer' }}>
            {copy.cta}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- FOOTER ---------- */
function Footer() {
  return (
    <footer style={{ borderTop: '1px solid var(--rule)', padding: '56px 0', background: 'var(--bg-2)' }}>
      <div className="wrap">
        <div className="footer-grid" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', gap: 40, alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Switch on={true} size={0.5} />
              <span style={{ fontFamily: 'var(--serif)', fontSize: 24, letterSpacing: '-0.02em' }}>EnAutomático</span>
            </div>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.55, maxWidth: 320 }}>
              Diagnóstico de IA productizado para pymes hispanohablantes. Barcelona · Buenos Aires. enautomatico.pro
            </p>
          </div>
          <FooterCol title="Producto" items={[
            { label: 'Ejemplo de informe', href: '#ejemplo' },
            { label: 'Precios',            href: '#precios' },
            { label: 'FAQ',                href: '#faq' }
          ]} />
          <FooterCol title="Legal" items={[
            { label: 'Aviso legal',            href: '/aviso-legal' },
            { label: 'Política de privacidad', href: '/privacidad' },
            { label: 'Política de cookies',    href: '/cookies' },
            { label: 'Condiciones',            href: '/condiciones' }
          ]} />
          <FooterCol title="Compañía" items={[
            { label: 'Sobre',                href: '#', disabled: true },
            { label: 'Casos · LinkedIn',     href: '#', disabled: true },
            { label: 'Partners white-label', href: '#', disabled: true },
            { label: 'Contacto',             href: 'mailto:hola@enautomatico.pro' }
          ]} />
        </div>
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid var(--rule)', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ink-3)', flexWrap: 'wrap', gap: 12 }}>
          <div>© 2026 EnAutomático · Barcelona ES · Tratamiento de datos bajo RGPD</div>
        </div>
      </div>
    </footer>);

}
function FooterCol({ title, items }) {
  return (
    <div>
      <div className="allcaps" style={{ color: 'var(--ink-3)', marginBottom: 14 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(({ label, href, disabled }) => (
          <a
            key={label}
            href={href}
            {...(disabled ? { 'aria-disabled': 'true', title: 'Próximamente', onClick: (e) => e.preventDefault() } : {})}
            style={{
              fontSize: 14,
              textDecoration: 'none',
              color: 'var(--ink-2)',
              ...(disabled ? { opacity: 0.55, cursor: 'not-allowed' } : {})
            }}
          >
            {label}
          </a>
        ))}
      </div>
    </div>);

}

function SectionLabel({ num, name, dark }) {
  return (
    <div className="section-label" style={dark ? { '--local-ink3': 'oklch(0.7 0.01 60)' } : {}}>
      <span className="num" style={dark ? { color: 'oklch(0.7 0.01 60)' } : {}}>{num}</span>
      <span className="name" style={dark ? { color: 'oklch(0.7 0.01 60)' } : {}}>{name}</span>
      <span className="line" style={dark ? { background: 'oklch(0.3 0.01 60)' } : {}} />
    </div>);

}

/* ---------- TWEAKS UI ---------- */
function TweaksPanel({ tweaks, setTweak }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === '__activate_edit_mode') setOn(true);
      if (e.data?.type === '__deactivate_edit_mode') setOn(false);
    };
    window.addEventListener('message', handler);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', handler);
  }, []);
  const update = (k, v) => {
    setTweak(k, v);
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [k]: v } }, '*');
  };
  return (
    <div className={`tw-panel ${on ? 'on' : ''}`}>
      <h4>Tweaks · EnAutomático</h4>
      <div className="tw-row">
        <label>Dialecto</label>
        <div className="tw-seg">
          <button className={tweaks.dialect === 'castellano' ? 'on' : ''} onClick={() => update('dialect', 'castellano')}>Castellano</button>
          <button className={tweaks.dialect === 'rioplatense' ? 'on' : ''} onClick={() => update('dialect', 'rioplatense')}>Rioplatense</button>
        </div>
      </div>
      <div className="tw-row">
        <label>Fase comercial</label>
        <div className="tw-seg">
          <button className={tweaks.phase === 'pilot' ? 'on' : ''} onClick={() => update('phase', 'pilot')}>Piloto €99</button>
          <button className={tweaks.phase === 'target' ? 'on' : ''} onClick={() => update('phase', 'target')}>Target €299</button>
        </div>
      </div>
      <div className="tw-row">
        <label>Tono del acento (hue oklch)</label>
        <input type="range" min="100" max="180" value={tweaks.accentHue} onChange={(e) => update('accentHue', +e.target.value)} style={{ width: '100%' }} />
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{tweaks.accentHue}° · verde interruptor</div>
      </div>
    </div>);

}

/* ---------- LANDING ---------- */
function Landing({ tweaks, setTweak }) {
  const onOpenBooking = () => navigateTo('/diagnostico');
  return (
    <>
      <Nav onOpenBooking={onOpenBooking} />
      <Hero dialect={tweaks.dialect} phase={tweaks.phase} onOpenBooking={onOpenBooking} />
      <IncluyeEnTuPlan dialect={tweaks.dialect} />
      <Proof />
      <SampleReport />
      {false && <VoiceAgent dialect={tweaks.dialect} />}
      <CompareRoutes dialect={tweaks.dialect} />
      <Pricing phase={tweaks.phase} dialect={tweaks.dialect} />
      <ProductosBento />
      <FAQ />
      <Book dialect={tweaks.dialect} phase={tweaks.phase} onOpenBooking={onOpenBooking} />
      <Footer />
      <TweaksPanel tweaks={tweaks} setTweak={setTweak} />
    </>
  );
}

/* ---------- APP / ROUTER ---------- */
function App() {
  const [tweaks, setTweaks] = useState(() => {
    const detected = detectInitialDialect();
    return detected ? { ...TWEAK_DEFAULTS, dialect: detected } : TWEAK_DEFAULTS;
  });
  const [route, setRoute] = useState(() => (typeof window !== 'undefined' ? window.location.pathname : '/'));
  const setTweak = (k, v) => setTweaks((t) => ({ ...t, [k]: v }));

  useEffect(() => {
    const onPop = () => setRoute(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--on', `oklch(0.72 0.15 ${tweaks.accentHue})`);
    document.documentElement.style.setProperty('--on-ink', `oklch(0.32 0.09 ${tweaks.accentHue})`);
  }, [tweaks.accentHue]);

  // Reveal-on-scroll: each <section> fades its children up the first time it
  // enters the viewport. The 'js-ready' class on <html> gates the hidden
  // initial state so JS-disabled visitors still see the page.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    if (!('IntersectionObserver' in window)) {
      // Old browsers: just reveal everything immediately.
      root.classList.add('js-ready');
      document.querySelectorAll('section').forEach((s) => s.classList.add('is-revealed'));
      return;
    }
    root.classList.add('js-ready');
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      }
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
    // Use rAF so the observer starts after the first paint, avoiding a flash
    // where the hero would briefly render hidden.
    const id = requestAnimationFrame(() => {
      document.querySelectorAll('section').forEach((s) => io.observe(s));
    });
    return () => {
      cancelAnimationFrame(id);
      io.disconnect();
    };
  }, [route]);

  // Honor optional dialect override from URL: /diagnostico?d=ar | ?d=es
  const dialectForRoute = (() => {
    if (typeof window === 'undefined') return tweaks.dialect;
    try {
      const sp = new URLSearchParams(window.location.search);
      const d = (sp.get('d') || '').toLowerCase();
      if (d === 'ar' || d === 'rioplatense') return 'rioplatense';
      if (d === 'es' || d === 'castellano') return 'castellano';
    } catch (e) {}
    return tweaks.dialect;
  })();

  if (route === '/diagnostico/confirmado') {
    return <DiagnosticoConfirmado dialect={dialectForRoute} />;
  }
  if (route === '/diagnostico') {
    return <DiagnosticoPage dialect={dialectForRoute} />;
  }
  if (route === '/privacidad') {
    return <PrivacidadPage dialect={dialectForRoute} />;
  }
  if (route === '/aviso-legal') {
    return <AvisoLegalPage dialect={dialectForRoute} />;
  }
  if (route === '/cookies') {
    return <CookiesPage dialect={dialectForRoute} />;
  }
  if (route === '/sofia-demo') {
    return <SofiaDemoPage />;
  }
  return <Landing tweaks={tweaks} setTweak={setTweak} />;
}

createRoot(document.getElementById('app')).render(<App />);
